import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, useContext, createContext, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Lightbulb, Megaphone,
  Images, Users, Sparkles, Loader2, Check,
  AlertTriangle, RefreshCw, Download, Copy, Hash, MessageSquare,
  ChevronLeft, ChevronRight,
  Heart, MessageCircle, Bookmark, Share2, Home, Search, Plus, Music2,
  User, Repeat2, SignalHigh, Wifi, BatteryFull,
  Pencil, AlignLeft, AlignCenter, AlignRight, Trash2, Info,
  ArrowUpToLine, ArrowDownToLine, FoldVertical, RotateCcw,
  Wand2, BookOpen, Package, Gift, X,
  CalendarClock, ChevronUp, ChevronDown,
} from "lucide-react";
import { PastRunsSection, formatRunWhen } from "@/components/past-runs-section";
import type { SlideshowExpressRun, SlideshowExpressSlide, SlideshowExpressStyleRecord, Persona, NarrativeAngle, Product, Offer } from "@shared/schema";
import { PickerSelect } from "@/components/picker-select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import {
  SLIDESHOW_OVERLAY_FONTS,
  resolveSlideTextStyle,
  overlayFontKeyForFamily,
  parseSlideOverlayOverrides,
  findOverlayFont,
  cleanOverlayText,
  layoutSlideText,
  hasOverlayPosition,
  mergeSlideTextSettings,
  resolveOverlayBlockTop,
  type SlideOverlayOverrides,
} from "@shared/slideshow-overlay";
import {
  parseSlideshowTextSettings,
  type SlideshowTextSettings,
} from "@shared/slideshow-text-settings";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { SLIDESHOW_EXPRESS_CANVAS } from "@shared/mcp-slideshow-express";

type ConfigStatus = {
  simApiKey: boolean;
  kie: boolean;
  r2: boolean;
  strategistUrl: boolean;
  conceptUrl: boolean;
  copyUrl: boolean;
  storyboardUrl: boolean;
  imagePromptUrl: boolean;
  captionUrl: boolean;
};

type RunDetail = { run: SlideshowExpressRun; slides: SlideshowExpressSlide[] };

// Statuses where the server is actively working (poll for progress).
// The two review gates ("awaiting_concept_review", "awaiting_copy_review") are
// deliberately NOT active: runs park there until the user approves — no
// polling needed.
const ACTIVE_RUN_STATUSES = new Set(["concept", "character", "strategy", "copy", "storyboard", "image_prompts", "rendering", "packaging"]);

// Statuses before slide copy has been approved. These runs render as compact
// cards in the side-by-side review carousel (processing spinner or review
// controls) instead of the tall stacked panels. Failed runs are deliberately
// NOT here — they surface in the stacked list with their error + retry action.
const PRE_APPROVAL_CAROUSEL_STATUSES = new Set([
  "concept",
  "awaiting_concept_review",
  "character",
  "strategy",
  "copy",
  "awaiting_copy_review",
  "storyboard",
  "awaiting_storyboard_review",
]);

function runStatusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  switch (status) {
    case "concept": return { label: "Writing concept", variant: "secondary" };
    case "awaiting_concept_review": return { label: "Concept awaiting your approval", variant: "outline" };
    case "character": return { label: "Creating character", variant: "secondary" };
    case "strategy": return { label: "Planning strategy", variant: "secondary" };
    case "copy": return { label: "Writing slide copy", variant: "secondary" };
    case "image_prompts": return { label: "Designing images", variant: "secondary" };
    case "rendering": return { label: "Rendering slides", variant: "secondary" };
    case "packaging": return { label: "Writing caption", variant: "secondary" };
    case "awaiting_copy_review": return { label: "Copy awaiting your approval", variant: "outline" };
    case "storyboard": return { label: "Creating storyboard", variant: "secondary" };
    case "awaiting_storyboard_review": return { label: "Storyboard awaiting your approval", variant: "outline" };
    case "ready": return { label: "Ready", variant: "default" };
    case "failed": return { label: "Failed", variant: "destructive" };
    default: return { label: status, variant: "outline" };
  }
}

// Deterministic, realistic-looking TikTok engagement numbers derived from the
// run id so the mock stays stable across renders (illustrative only).
function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function deriveMetrics(id: string): { likes: number; comments: number; saves: number } {
  const h = hashStr(id || "seed");
  return {
    likes: 25000 + (h % 875000),
    comments: 120 + ((h >>> 3) % 9800),
    saves: 800 + ((h >>> 7) % 48000),
  };
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 10_000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

function handleFromName(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return slug || "brand";
}

// Recursively search a parsed agent payload for the first value under one of
// `keys` that satisfies `pred` (mirrors the server-side deepFind). Tolerates the
// copy agent wrapping its output under `slideshow_copy_package` or similar.
function findDeep(obj: any, keys: string[], pred: (v: any) => boolean, depth = 6): any {
  if (obj == null || depth < 0) return undefined;
  if (Array.isArray(obj)) {
    for (const el of obj) {
      const found = findDeep(el, keys, pred, depth - 1);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  if (typeof obj === "object") {
    for (const k of keys) {
      if (k in obj && pred((obj as any)[k])) return (obj as any)[k];
    }
    for (const v of Object.values(obj)) {
      const found = findDeep(v, keys, pred, depth - 1);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

// Extract a slide's on-screen text tolerating the copy agent's field naming.
function slideOverlayText(s: any): string {
  if (typeof s === "string") return s.trim();
  if (!s || typeof s !== "object") return "";
  const v = s.text ?? s.overlay_text ?? s.overlayText ?? s.slide_text ?? s.slideText ?? s.copy ?? "";
  return typeof v === "string" ? v.trim() : "";
}

// Pull the reviewable concept text out of a run's persisted conceptJson for
// the concept review gate (mirrors the server-side extractConceptText).
function extractConceptReviewText(conceptJson: any): string {
  if (typeof conceptJson === "string") return conceptJson.trim();
  const v = findDeep(
    conceptJson,
    ["concept", "concept_text", "conceptText", "angle", "big_idea", "bigIdea", "hook", "idea", "theme", "summary"],
    (x) => typeof x === "string" && x.trim().length > 0,
  );
  return typeof v === "string" ? v.trim() : "";
}

// Display-only: split a "•"-separated concept string into a bulleted list.
// The underlying raw string (editing, copy, server payloads) is untouched.
function ConceptBullets({ text }: { text: string }) {
  const segments = text.split("•").map((s) => s.trim()).filter(Boolean);
  if (segments.length <= 1) return <>{text}</>;
  return (
    <ul className="list-disc space-y-1 pl-4">
      {segments.map((seg, i) => (
        <li key={i}>{seg}</li>
      ))}
    </ul>
  );
}

// Pull the reviewable concept/angle + per-slide on-screen text out of a run's
// persisted copyJson for the review gate.
// Legacy runs persisted before the server-side sanitizer may still carry the
// Copy Agent's parroted "APPROVED/USER-PROVIDED CONCEPT (MANDATORY)… Concept: …"
// steering wrapper in their concept field — strip it at display time (mirrors
// stripConceptSteeringWrapper in server/services/slideshow-express-worker.ts).
function stripConceptSteeringWrapper(text: string): string {
  if (typeof text !== "string") return text as any;
  const trimmed = text.trimStart();
  if (!/^(?:APPROVED|USER-PROVIDED) CONCEPT \(MANDATORY\)/.test(trimmed)) return text;
  const m = trimmed.match(/\bConcept:\s*([\s\S]*)$/);
  if (!m) return "";
  let rest = m[1];
  const stanzaIdx = rest.search(/\n\s*\n[A-Z][A-Z0-9 /&'()-]*\(MANDATORY\)/);
  if (stanzaIdx >= 0) rest = rest.slice(0, stanzaIdx);
  return rest.trim();
}

function extractCopyReview(copyJson: any): { concept: string; slides: string[] } {
  const rawConcept =
    findDeep(
      copyJson,
      ["concept", "angle", "hook", "big_idea", "bigIdea", "theme", "idea", "summary"],
      (v) => typeof v === "string" && v.trim().length > 0,
    ) || "";
  const concept =
    typeof rawConcept === "string" ? stripConceptSteeringWrapper(rawConcept) : rawConcept;
  const slidesArr: any[] = findDeep(copyJson, ["slides"], Array.isArray) ?? [];
  return {
    concept: typeof concept === "string" ? concept.trim() : "",
    slides: slidesArr.map(slideOverlayText),
  };
}

export default function SlideshowExpressPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const canManageStyles = !!user?.isMaster || !!user?.isSuperuser;

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [customConcept, setCustomConcept] = useState("");
  const [ctaText, setCtaText] = useState("");
  // Optional persona targeting (mirrors Ads Express). Empty = the
  // fully-automatic untargeted path, unchanged.
  const [personaId, setPersonaId] = useState<string | null>(null);
  // Narrative-angle input, mirroring Ads Express: "saved" picks one of the
  // brand's saved narrative angles; "custom" is the free-form concept
  // direction textarea (the former standalone "Concept direction" field).
  const [angleMode, setAngleMode] = useState<"saved" | "custom">("saved");
  const [angleId, setAngleId] = useState("");
  // Optional promoted product/offer (Task #1530) — pill toggle mirrors
  // Ads Express. Nothing selected keeps the brand-only pipeline unchanged.
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [productId, setProductId] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<string | null>(null);

  const { data: config } = useQuery<ConfigStatus>({
    queryKey: [`/api/brands/${brandId}/slideshow-express/config-status`],
    enabled: !!brandId,
  });
  const { data: brand } = useQuery<{ id: string; name: string }>({
    queryKey: [`/api/brands/${brandId}`],
    enabled: !!brandId,
  });
  // Like the style-bank query below, override the global query defaults
  // (retry:false + staleTime:Infinity + no focus refetch) which would cache a
  // single transient failure (e.g. a dev-server restart mid-run) forever and
  // wedge the list on a stale status — the concept/copy review gates would
  // then never appear without a hard refresh.
  const { data: runs = [], isLoading: runsLoading } = useQuery<SlideshowExpressRun[]>({
    queryKey: [`/api/brands/${brandId}/slideshow-express/runs`],
    enabled: !!brandId,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      // Keep polling through errors: if the latest fetch failed we can't trust
      // the cached snapshot, so poll until we get a clean read again.
      if (query.state.status === "error" || query.state.fetchFailureCount > 0) return 5000;
      const rows = (query.state.data as SlideshowExpressRun[] | undefined) ?? [];
      return rows.some((r) => ACTIVE_RUN_STATUSES.has(r.status)) ? 5000 : false;
    },
  });
  // The style bank is now DB-backed and admin-editable. Override the global
  // query defaults (retry:false + staleTime:Infinity) which would cache a
  // transient first-paint 401 forever and wedge this shared endpoint empty.
  const { data: styles = [], isLoading: stylesLoading } = useQuery<SlideshowExpressStyleRecord[]>({
    queryKey: ["/api/slideshow-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
  });

  const { data: personas = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  // Optional promoted product/offer pickers (Task #1530).
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  // The brand's saved narrative angles for the "Saved angle" picker mode.
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });

  // Angles offered in the dropdown: never archived, and always scoped to the
  // selected persona. With no persona picked there are NO saved angles to
  // choose (angles are persona-scoped records) — the picker stays locked.
  const availableAngles = useMemo(
    () =>
      personaId
        ? angles.filter((a) => a.status !== "archived" && a.personaId === personaId)
        : [],
    [angles, personaId],
  );
  const selectedAngle = availableAngles.find((a) => a.id === angleId) ?? null;

  // Group by Opportunity Group (`family`) — same grouping as Ads Express.
  const angleGroups = useMemo(
    () =>
      groupAnglesByFamily(availableAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    [availableAngles],
  );

  // If changing persona invalidates the chosen angle, clear it so we don't
  // quietly steer with a mismatched angle on Generate.
  useEffect(() => {
    if (angleId && !availableAngles.some((a) => a.id === angleId)) {
      setAngleId("");
    }
  }, [angleId, availableAngles]);
  const missingUrls = config
    ? [
        !config.strategistUrl && "Creative Strategist",
        !config.conceptUrl && "Slideshow Concept",
        !config.copyUrl && "Slideshow Copy",
        !config.imagePromptUrl && "Image Prompt + Render Guidance",
        !config.captionUrl && "TikTok Caption + Final Package",
      ].filter(Boolean) as string[]
    : [];
  const missingInfra = config
    ? [!config.simApiKey && "Sim API key", !config.kie && "KIE", !config.r2 && "R2 storage"].filter(Boolean) as string[]
    : [];
  const notConfigured = missingUrls.length > 0 || missingInfra.length > 0;

  const createRun = useMutation({
    mutationFn: async () => {
      // Whichever angle mode is active, the resulting TEXT flows through the
      // exact same optional `customConcept` → concept-direction-steering path
      // the free-form textarea always used (no server/Sim contract change).
      // Saved mode resolves the picked angle to its name + summary; nothing
      // selected sends no direction at all — byte-identical to today.
      const resolvedConcept =
        angleMode === "saved"
          ? selectedAngle
            ? [selectedAngle.name?.trim(), selectedAngle.summary?.trim()].filter(Boolean).join(" — ")
            : ""
          : customConcept.trim();
      const res = await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs`, {
        styleId: selectedStyleId,
        customConcept: resolvedConcept || undefined,
        // When a SAVED narrative angle is chosen, also pass its id so the
        // concept generator anchors the five concepts in that deliberate
        // strategic angle (stronger than the free-form "concept direction"
        // steering). Custom / free-form mode sends no angle id.
        narrativeAngleId:
          angleMode === "saved" && selectedAngle ? selectedAngle.id : undefined,
        ctaText: ctaText.trim() || undefined,
        personaId: personaId || undefined,
        // Optional promoted product/offer (Task #1530). Nothing
        // selected sends none of these — brand-only path unchanged.
        promotedItemType: promotedItemId ? promotedItemType : undefined,
        productId: promotedItemType === "product" ? productId || undefined : undefined,
        offerId: promotedItemType === "offer" ? offerId || undefined : undefined,
      });
      return (await res.json()) as { runs: SlideshowExpressRun[] };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`] });
      setSelectedStyleId(null);
      setCustomConcept("");
      setCtaText("");
      setPersonaId(null);
      setAngleId("");
      setProductId(null);
      setOfferId(null);
      const count = data?.runs?.length ?? 0;
      toast({
        title: count > 1 ? `Generating ${count} concepts` : "Generating slideshow",
        description:
          count > 1
            ? `${count} distinct concepts are being written for this style — you'll review and approve each before any images render. Earlier runs moved to Past runs below.`
            : "Your TikTok slideshow post is being created. Earlier runs moved to Past runs below.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e?.message || "Failed to create run", variant: "destructive" });
    },
  });

  // Single-select (radio behavior): clicking the selected style deselects it.
  const toggleStyle = (styleId: string) => {
    setSelectedStyleId((prev) => (prev === styleId ? null : styleId));
  };

  const isOfferMode = promotedItemType === "offer";
  const promotedItemId = isOfferMode ? offerId : productId;

  const canGenerate = !!selectedStyleId && !createRun.isPending && !notConfigured;

  return (
    <div className="mx-auto max-w-5xl space-y-5 px-8 py-6">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Images className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">Slideshow Express</h1>
          <p className="text-sm text-muted-foreground">
            Pick one or more slideshow styles to generate organic TikTok slideshows from your brand DNA — one post per selected style, each with five 4:5 image slides, baked-in text, caption, hashtags, and a pinned comment. Earlier batches stay available under Past runs.
          </p>
        </div>
      </div>

      {notConfigured && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="banner-not-configured">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">Slideshow Express isn't fully configured yet.</p>
              {missingUrls.length > 0 && (
                <p className="text-muted-foreground">
                  Missing Sim workflow URL{missingUrls.length > 1 ? "s" : ""}: {missingUrls.join(", ")}. Set them in the Admin dashboard.
                </p>
              )}
              {missingInfra.length > 0 && (
                <p className="text-muted-foreground">Missing: {missingInfra.join(", ")}.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Builder — same visual language as the Ads Express input card:
          gradient card, icon header, PickerSelect-style fields. */}
      <Card className="relative overflow-hidden border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)] bg-gradient-to-b from-card to-muted/30 dark:from-card dark:to-background/40">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl"
        />
        <CardContent className="relative space-y-5 p-6">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
              <Wand2 className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Choose one or more slideshow styles below — everything else comes from your brand DNA
              </p>
            </div>
            {/* Product/Offer pill toggle — mirrors Ads Express. Switching
                modes clears the other mode's selection so only ONE id is ever
                sent. */}
            <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5 shrink-0">
              <button
                type="button"
                onClick={() => { setPromotedItemType("product"); setOfferId(null); }}
                disabled={createRun.isPending}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  promotedItemType === "product"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="button-promoted-type-product"
              >
                <Package className="h-3.5 w-3.5" /> Product
              </button>
              <button
                type="button"
                onClick={() => { setPromotedItemType("offer"); setProductId(null); }}
                disabled={createRun.isPending}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  promotedItemType === "offer"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="button-promoted-type-offer"
              >
                <Gift className="h-3.5 w-3.5" /> Offer
              </button>
            </div>
          </div>

          {/* Optional creative controls — everything blank keeps the
              fully-automatic path unchanged (auto concept, agent-chosen CTA). */}
          <div className="relative grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <PickerSelect
                icon={<Users className="h-4 w-4" />}
                label="Persona (optional)"
                placeholder="No persona — brand-wide"
                value={personaId ?? ""}
                onChange={(v) => setPersonaId(v || null)}
                onClear={() => setPersonaId(null)}
                options={personas.map((p) => ({
                  value: p.id,
                  label: p.name,
                  imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null,
                }))}
                testId="select-persona"
                searchable
                optional
                disabled={createRun.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Optional. The 5 concepts are written to resonate with this persona. Leave unset for brand-wide concepts.
              </p>
            </div>
            {/* Optional promoted product/offer (Task #1530). The pill toggle
                in the card header switches which list this picker shows. */}
            <div className="space-y-1.5">
              <PickerSelect
                icon={isOfferMode ? <Gift className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                label={isOfferMode ? "Offer (optional)" : "Product (optional)"}
                placeholder={
                  isOfferMode
                    ? offers.length === 0
                      ? "No offers yet — add one in Products & Offers"
                      : "No offer — brand-wide"
                    : products.length === 0
                      ? "No products yet — add one in Products & Offers"
                      : "No product — brand-wide"
                }
                value={promotedItemId ?? ""}
                onChange={(v) => {
                  const next = v || null;
                  if (isOfferMode) setOfferId(next);
                  else setProductId(next);
                }}
                onClear={() => {
                  if (isOfferMode) setOfferId(null);
                  else setProductId(null);
                }}
                options={(isOfferMode ? offers : products).map((item) => {
                  const media = isOfferMode ? null : (item as { mediaJson?: string[] | null }).mediaJson;
                  const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
                  return {
                    value: item.id,
                    label: item.name,
                    description: (item as { summary?: string | null }).summary ?? null,
                    ...(isOfferMode ? {} : { imageUrl: first }),
                  };
                })}
                testId="select-promoted-item"
                searchable
                optional
                disabled={createRun.isPending || (isOfferMode ? offers.length === 0 : products.length === 0)}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Concepts, copy, and slide imagery center on this {isOfferMode ? "offer" : "product"}. Leave unset for brand-wide slideshows.
              </p>
            </div>
            <div className="space-y-1.5">
              {/* The CTA label + textarea occupy the SAME fixed 72px block
                  height as the persona picker box beside it, so the label
                  top-aligns with the persona dropdown AND the textarea bottom
                  lines up with the persona box bottom (fixed height so flex-1
                  shrinks the textarea instead of it keeping its 2-row height). */}
              <div className="flex h-[72px] flex-col">
                <Label htmlFor="input-cta-text" className="mb-1.5 whitespace-nowrap text-xs font-medium">
                  Last-slide CTA <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="input-cta-text"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value.replace(/[\r\n]+/g, " "))}
                  disabled={createRun.isPending}
                  placeholder="e.g. Shop now — link in bio"
                  className="min-h-0 flex-1 resize-none text-sm"
                  maxLength={300}
                  data-testid="input-cta-text"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Exact call-to-action shown on the final slide. Leave blank to let the AI choose.
              </p>
            </div>

            {/* Narrative-angle input — same pill toggle as Ads Express: pick a
                saved narrative angle OR type a free-form concept direction.
                Either way the resulting text steers the 5 generated concepts
                through the existing concept-direction path. */}
            <div className="space-y-2 md:col-span-2">
              <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5">
                <button
                  type="button"
                  onClick={() => setAngleMode("saved")}
                  disabled={createRun.isPending}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                    angleMode === "saved"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  data-testid="button-angle-mode-saved"
                >
                  <BookOpen className="h-3.5 w-3.5" /> Saved angle
                </button>
                <button
                  type="button"
                  onClick={() => setAngleMode("custom")}
                  disabled={createRun.isPending}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                    angleMode === "custom"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  data-testid="button-angle-mode-custom"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Custom concept angle
                </button>
              </div>

              {angleMode === "saved" ? (
                <div className="space-y-1.5">
                  <PickerSelect
                    icon={<Sparkles className="h-4 w-4" />}
                    label="Narrative Angle (optional)"
                    placeholder={
                      !personaId
                        ? "Select a persona to see its saved angles"
                        : availableAngles.length === 0
                          ? "No saved angles for this persona"
                          : "Choose narrative angle"
                    }
                    value={angleId}
                    onChange={setAngleId}
                    groups={angleGroups}
                    testId="select-angle"
                    searchable
                    wrapDescription
                    optional
                    disabled={createRun.isPending || !personaId || availableAngles.length === 0}
                  />
                  <p className="text-xs text-muted-foreground" data-testid="text-angle-helper">
                    {!personaId
                      ? "Select a target persona above to choose from its saved angles, or switch to Custom concept angle."
                      : "Optional. The chosen angle guides the 5 generated concepts — each stays distinct but takes influence from it. Leave unset and the AI invents the angles."}
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="input-custom-concept" className="text-xs font-medium">
                    Custom concept angle
                  </Label>
                  <Textarea
                    id="input-custom-concept"
                    value={customConcept}
                    onChange={(e) => setCustomConcept(e.target.value)}
                    disabled={createRun.isPending}
                    placeholder="Describe a direction to guide the 5 generated concepts — e.g. “Morning-routine stories showing how our serum fits into a 5-minute skincare ritual”. Leave blank to let the AI invent the angles."
                    className="min-h-[84px] resize-y text-sm"
                    maxLength={4000}
                    data-testid="input-custom-concept"
                  />
                  <p className="text-xs text-muted-foreground" data-testid="text-concept-helper">
                    Optional. Used to guide the 5 generated concepts — each stays distinct but takes influence from your direction. Leave blank and the AI invents the angles. You'll review and approve each concept and its slide copy before images are created.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Slideshow style bank — single-select cards. The batch fans out
              five parallel runs (distinct concepts) for the ONE chosen style. */}
          <div className="space-y-3 pt-1">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold tracking-tight flex items-center gap-1.5">
                  Slideshow style
                  {!selectedStyleId && <span className="text-muted-foreground/40">*</span>}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Choose one narrative style — five slideshows with distinct concepts are generated for it.
                </p>
              </div>
              {selectedStyleId && (
                <button
                  type="button"
                  onClick={() => setSelectedStyleId(null)}
                  className="text-xs text-muted-foreground hover:text-foreground shrink-0"
                  data-testid="button-clear-styles"
                >
                  Clear
                </button>
              )}
            </div>
            {stylesLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-[4/5] w-full rounded-xl" data-testid={`skeleton-style-${i}`} />
                ))}
              </div>
            ) : styles.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/60 py-10 text-center text-sm text-muted-foreground" data-testid="text-no-styles">
                No slideshow styles yet.{canManageStyles ? " Add styles in Admin \u2192 Slideshow Styles." : ""}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {styles.map((style) => {
                  const checked = selectedStyleId === style.value;
                  return (
                    <button
                      type="button"
                      key={style.id}
                      onClick={() => toggleStyle(style.value)}
                      disabled={createRun.isPending}
                      data-testid={`card-style-${style.value}`}
                      aria-pressed={checked}
                      className={cn(
                        "group relative flex flex-col overflow-hidden rounded-xl border text-left transition-all duration-150",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        "disabled:opacity-60 disabled:cursor-not-allowed",
                        checked
                          ? "border-primary ring-2 ring-primary/30 shadow-sm"
                          : "border-border/60 hover:border-primary/40 hover:shadow-sm",
                      )}
                    >
                      <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                        {/* Fallback placeholder — only visible when no preview image loads */}
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-muted-foreground/40">
                          <Images className="h-8 w-8" />
                        </div>
                        {style.imageUrl ? (
                          <img
                            src={style.imageUrl}
                            alt={style.label}
                            loading="lazy"
                            className="relative h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                          />
                        ) : null}
                        {checked && (
                          <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col gap-0.5 p-2.5">
                        <span className="text-xs font-semibold leading-tight">{style.label}</span>
                        <span className="text-[11px] leading-snug text-muted-foreground line-clamp-2">
                          {style.shortDescription}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Five slideshow posts — each with its own concept — for the selected style. Earlier batches move to Past runs below.
            </p>
            <Button onClick={() => createRun.mutate()} disabled={!canGenerate} data-testid="button-generate">
              {createRun.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Generate 5 concepts</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch scoping (Task #1633) — prior runs are no longer deleted, so the
          "current" view is the NEWEST batch only (runs sharing the newest
          run's batchId; legacy null-batchId rows group as one legacy batch).
          Everything else lists under Past runs below. */}
      {(() => {
        const sorted = [...runs].sort(
          (a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime(),
        );
        const currentBatchId = sorted[0] ? ((sorted[0] as any).batchId ?? null) : null;
        const inCurrentBatch = (r: SlideshowExpressRun) =>
          sorted.length > 0 && ((r as any).batchId ?? null) === currentBatchId;
        const currentRuns = sorted.filter(inCurrentBatch);
        const pastRuns = sorted.filter((r) => !inCurrentBatch(r));
        const otherRuns = currentRuns.filter((r) => !PRE_APPROVAL_CAROUSEL_STATUSES.has(r.status));
        return (
          <>
            {/* Pre-approval review — runs parked at the concept/copy gates render as
                side-by-side review cards in a horizontal carousel. Approved /
                rendering / completed runs keep the classic stacked panels below. */}
            <ConceptReviewSection brandId={brandId!} runs={currentRuns} styles={styles} />

            {/* Runs */}
            {!(!runsLoading && currentRuns.length > 0 && otherRuns.length === 0) && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Runs</h2>
                {runsLoading ? (
                  <div className="space-y-4"><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>
                ) : currentRuns.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                      <Images className="h-10 w-10 opacity-40" />
                      <p className="text-sm">No slideshows yet. Pick one or more styles above to generate your first batch.</p>
                    </CardContent>
                  </Card>
                ) : (
                  otherRuns.map((run) => (
                    <RunPanel
                      key={run.id}
                      brandId={brandId!}
                      run={run}
                      brandName={brand?.name}
                      personaName={run.personaId ? personas.find((p) => p.id === run.personaId)?.name : undefined}
                    />
                  ))
                )}
              </div>
            )}

            {/* Past runs — older batches remain viewable (Task #1633). */}
            <SlideshowPastRuns
              brandId={brandId!}
              pastRuns={pastRuns}
              isLoading={runsLoading}
              styles={styles}
              brandName={brand?.name}
              personas={personas}
            />
          </>
        );
      })()}
    </div>
  );
}

// ── Past runs history (Task #1633) ──────────────────────────────────────────

function slideshowRunStatusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  switch (status) {
    case "completed": return { label: "Completed", variant: "default" };
    case "failed": return { label: "Failed", variant: "destructive" };
    case "awaiting_concept_review": return { label: "Awaiting concept review", variant: "outline" };
    case "awaiting_copy_review": return { label: "Awaiting copy review", variant: "outline" };
    case "awaiting_storyboard_review": return { label: "Awaiting storyboard review", variant: "outline" };
    default:
      return ACTIVE_RUN_STATUSES.has(status)
        ? { label: "Running", variant: "secondary" }
        : { label: status, variant: "outline" };
  }
}

function SlideshowPastRuns({
  brandId,
  pastRuns,
  isLoading,
  styles,
  brandName,
  personas,
}: {
  brandId: string;
  pastRuns: SlideshowExpressRun[];
  isLoading: boolean;
  styles: SlideshowExpressStyleRecord[];
  brandName?: string;
  personas: Persona[];
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <PastRunsSection title="Past runs" count={pastRuns.length} isLoading={isLoading} testIdPrefix="slideshow-past-runs">
      {pastRuns.map((run) => {
        const meta = slideshowRunStatusMeta(run.status);
        const styleLabel = styles.find((s) => s.value === run.styleId)?.label || run.styleId;
        const isOpen = expandedId === run.id;
        return (
          <div key={run.id} className="rounded-lg border border-border/50 bg-card" data-testid={`card-past-run-${run.id}`}>
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : run.id)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
              data-testid={`button-toggle-past-run-${run.id}`}
            >
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`text-past-run-date-${run.id}`}>
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatRunWhen(run.createdAt as any)}
                </span>
                <Badge variant={meta.variant} className="text-[10px]" data-testid={`status-past-run-${run.id}`}>
                  {meta.label}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{styleLabel}</Badge>
                <span className="text-xs text-muted-foreground truncate">{run.name}</span>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-1">
                <RunPanel
                  brandId={brandId}
                  run={run}
                  brandName={brandName}
                  personaName={run.personaId ? personas.find((p) => p.id === run.personaId)?.name : undefined}
                />
              </div>
            )}
          </div>
        );
      })}
    </PastRunsSection>
  );
}

// ── Concept-box height sync (Task #1637) ────────────────────────────────────
// Each review card's read-only "Concept / angle" box reports its natural
// content height; the carousel shares the max back down so every card's box
// renders at the same height (tallest wins), keeping the sections below
// aligned across columns. Very long text scrolls internally past the cap.
const CONCEPT_BOX_MAX_PX = 240;
const ConceptBoxSyncContext = createContext<{
  report: (id: string, height: number | null) => void;
  maxHeight: number;
} | null>(null);

function useConceptBoxSync() {
  const [heights, setHeights] = useState<Record<string, number>>({});
  const report = useCallback((id: string, height: number | null) => {
    setHeights((prev) => {
      if (height == null) {
        if (!(id in prev)) return prev;
        const { [id]: _gone, ...rest } = prev;
        return rest;
      }
      if (prev[id] === height) return prev;
      return { ...prev, [id]: height };
    });
  }, []);
  const maxHeight = Math.min(
    CONCEPT_BOX_MAX_PX,
    Object.values(heights).reduce((m, h) => Math.max(m, h), 0),
  );
  return useMemo(() => ({ report, maxHeight }), [report, maxHeight]);
}

// ── Pre-approval copy review — side-by-side card carousel ───────────────────
// Runs parked at either review gate (awaiting_concept_review /
// awaiting_copy_review) render as cards side by side, scrolled horizontally
// with arrows + dots, per the "Copy review (concepts only)" mockup. Approve /
// Edit / Regenerate wire to the SAME endpoints the stacked panels used.
function ConceptReviewSection({
  brandId, runs, styles,
}: {
  brandId: string;
  runs: SlideshowExpressRun[];
  styles: SlideshowExpressStyleRecord[];
}) {
  // Number concepts by creation order across the current batch (this section
  // only receives the newest batch's runs), so "Concept 3" stays stable as
  // siblings approve out.
  const ascending = useMemo(
    () => [...runs].sort((a, b) => new Date(a.createdAt as any).getTime() - new Date(b.createdAt as any).getTime()),
    [runs],
  );
  const conceptNumberById = useMemo(() => {
    const m = new Map<string, number>();
    ascending.forEach((r, i) => m.set(r.id, i + 1));
    return m;
  }, [ascending]);
  const reviewRuns = useMemo(
    () => ascending.filter((r) => PRE_APPROVAL_CAROUSEL_STATUSES.has(r.status)).reverse(),
    [ascending],
  );

  const [cardsPerView, setCardsPerView] = useState(3);
  const conceptBoxSync = useConceptBoxSync();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [firstVisible, setFirstVisible] = useState(0);
  const GAP = 16; // matches gap-4 on the scroller

  const cardStep = () => {
    const el = scrollerRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    return first ? first.offsetWidth + GAP : 0;
  };
  const scrollToCard = (i: number) => {
    const el = scrollerRef.current;
    const step = cardStep();
    if (!el || !step) return;
    const clamped = Math.max(0, Math.min(reviewRuns.length - 1, i));
    el.scrollTo({ left: clamped * step, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = scrollerRef.current;
    const step = cardStep();
    if (!el || !step) return;
    setFirstVisible(Math.max(0, Math.min(reviewRuns.length - 1, Math.round(el.scrollLeft / step))));
  };
  // Keep the tracked index sane when cards approve out or layout changes.
  useEffect(() => {
    if (firstVisible > Math.max(0, reviewRuns.length - 1)) {
      setFirstVisible(Math.max(0, reviewRuns.length - 1));
    }
  }, [reviewRuns.length, firstVisible]);

  if (reviewRuns.length === 0) return null;

  // "Concept review" until any run has an approved concept (i.e. has moved on
  // to copy writing or the copy gate); processing-only runs stay concept-stage.
  const allConceptGate = reviewRuns.every(
    (r) => r.status === "concept" || r.status === "awaiting_concept_review",
  );
  const total = reviewRuns.length;
  const lastVisible = Math.min(firstVisible + cardsPerView, total);
  const canPrev = firstVisible > 0;
  const canNext = firstVisible + cardsPerView < total;
  const styleLabelFor = (styleId: string | null | undefined) =>
    styles.find((s) => s.value === styleId)?.label || "Slideshow";

  return (
    <div className="space-y-4" data-testid="section-copy-review">
      {/* Header + info banner */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Sparkles className="mt-1 h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="text-copy-review-title">
              {allConceptGate ? "Concept review" : "Copy review (concepts only)"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {allConceptGate
                ? "Review each concept before any slide copy is written."
                : "Review concept and slide copy before generating images."}
            </p>
          </div>
        </div>
        <div className="flex max-w-md items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5 text-sm" data-testid="banner-copy-review-info">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-muted-foreground">
            You're reviewing concepts and on-screen text only. Images will be generated after approval.
          </p>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative">
        {canPrev && (
          <button
            type="button"
            onClick={() => scrollToCard(firstVisible - 1)}
            className="absolute -left-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border bg-background shadow-md hover:bg-accent"
            aria-label="Previous concepts"
            data-testid="button-review-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {canNext && (
          <button
            type="button"
            onClick={() => scrollToCard(firstVisible + 1)}
            className="absolute -right-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border bg-background shadow-md hover:bg-accent"
            aria-label="Next concepts"
            data-testid="button-review-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="carousel-copy-review"
        >
          <ConceptBoxSyncContext.Provider value={conceptBoxSync}>
            {reviewRuns.map((run) => (
              <div
                key={run.id}
                className="shrink-0 snap-start"
                style={{ flexBasis: `calc((100% - ${(cardsPerView - 1) * GAP}px) / ${cardsPerView})` }}
              >
                <ConceptReviewCard
                  brandId={brandId}
                  run={run}
                  conceptNumber={conceptNumberById.get(run.id) ?? 1}
                  styleLabel={styleLabelFor(run.styleId)}
                />
              </div>
            ))}
          </ConceptBoxSyncContext.Provider>
        </div>
      </div>

      {/* Pagination dots */}
      {total > 1 && (
        <div className="flex justify-center gap-1.5" data-testid="dots-copy-review">
          {reviewRuns.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onClick={() => scrollToCard(i)}
              aria-label={`Go to concept card ${i + 1}`}
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                i >= firstVisible && i < firstVisible + cardsPerView ? "bg-primary" : "bg-muted-foreground/25 hover:bg-muted-foreground/50",
              )}
              data-testid={`dot-copy-review-${i}`}
            />
          ))}
        </div>
      )}

      {/* Bottom bar: tip + range + cards-per-view */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-2.5">
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lightbulb className="h-3.5 w-3.5 shrink-0" />
          Tip: Use Edit to tweak copy in a concept. Use Regenerate to get a new take.
        </p>
        <div className="flex items-center gap-4">
          <span className="text-xs text-muted-foreground" data-testid="text-review-range">
            Concepts <span className="font-medium text-foreground">{Math.min(firstVisible + 1, total)}–{lastVisible}</span> of {total}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Show</span>
            <Select value={String(cardsPerView)} onValueChange={(v) => setCardsPerView(Number(v))}>
              <SelectTrigger className="h-8 w-[104px] text-xs" data-testid="select-cards-per-view">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 card</SelectItem>
                <SelectItem value="2">2 cards</SelectItem>
                <SelectItem value="3">3 cards</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

// One concept review card. Handles BOTH gates: the concept gate
// (awaiting_concept_review — concept only, approve-concept/regenerate-concept)
// and the copy gate (awaiting_copy_review — concept + per-slide text +
// multi-option selection, approve-copy/regenerate-copy). Edit toggles inline
// textareas; approving always sends the current (possibly edited) values —
// exactly the edit-then-approve behavior the stacked panels had.
function ConceptReviewCard({
  brandId, run, conceptNumber, styleLabel,
}: {
  brandId: string;
  run: SlideshowExpressRun;
  conceptNumber: number;
  styleLabel: string;
}) {
  const { toast } = useToast();

  // Same hardened per-run detail query as RunPanel (shared cache key).
  const { data } = useQuery<RunDetail>({
    queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/brands/${brandId}/slideshow-express/runs/${run.id}`);
      return (await res.json()) as RunDetail;
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (query.state.status === "error" || query.state.fetchFailureCount > 0) return 5000;
      const latest = (query.state.data as RunDetail | undefined)?.run ?? run;
      // Keep polling while the concept's storyboard preview is still
      // rendering (the run itself is parked at the gate — not "active").
      if (latest.storyboardStatus === "pending" || latest.storyboardStatus === "generating") return 5000;
      return ACTIVE_RUN_STATUSES.has(latest.status) ? 5000 : false;
    },
  });
  const detailRun = data?.run ?? run;
  const isConceptGate = detailRun.status === "awaiting_concept_review";
  const isCopyGate = detailRun.status === "awaiting_copy_review";
  // Task #1566 — the storyboard gate: parked awaiting approval, OR parked at
  // the storyboard stage with a FAILED storyboard (surfaced here with retry).
  const isStoryboardFailed = detailRun.status === "storyboard" && detailRun.storyboardStatus === "failed";
  const isStoryboardGate = detailRun.status === "awaiting_storyboard_review" || isStoryboardFailed;

  const invalidateRun = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`] });
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
  };

  const [editing, setEditing] = useState(false);

  // ── Concept gate state ──
  const generatedConcept = extractConceptReviewText(detailRun.conceptJson);
  const [editReviewConcept, setEditReviewConcept] = useState("");
  const conceptSeedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isConceptGate) {
      conceptSeedRef.current = null;
      return;
    }
    const sig = `${run.id}|${generatedConcept}`;
    if (conceptSeedRef.current === sig) return;
    conceptSeedRef.current = sig;
    setEditReviewConcept(generatedConcept);
  }, [isConceptGate, run.id, generatedConcept]);

  // ── Copy gate state (incl. multi-option) ──
  const copyOptions: any[] = Array.isArray(detailRun.copyOptionsJson) ? (detailRun.copyOptionsJson as any[]) : [];
  const [reviewOptionIdx, setReviewOptionIdx] = useState<number | null>(null);
  const effectiveOptionIdx = reviewOptionIdx ?? (detailRun.selectedCopyOption ?? 0);
  const reviewSource =
    copyOptions.length > 1 && copyOptions[effectiveOptionIdx]
      ? copyOptions[effectiveOptionIdx]
      : detailRun.copyJson;
  const reviewCopy = extractCopyReview(reviewSource);
  const [editConcept, setEditConcept] = useState("");
  const [editSlides, setEditSlides] = useState<string[]>([]);
  const [reviewDirty, setReviewDirty] = useState(false);
  const seedSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isCopyGate) {
      seedSigRef.current = null;
      setReviewDirty(false);
      setReviewOptionIdx(null);
      return;
    }
    const sig = `${run.id}|${effectiveOptionIdx}|${JSON.stringify(reviewCopy)}`;
    if (seedSigRef.current === sig) return;
    const optionOrRunChanged =
      !seedSigRef.current || !seedSigRef.current.startsWith(`${run.id}|${effectiveOptionIdx}|`);
    if (reviewDirty && !optionOrRunChanged) return; // keep the user's edits
    seedSigRef.current = sig;
    setEditConcept(reviewCopy.concept);
    setEditSlides(reviewCopy.slides);
    setReviewDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCopyGate, run.id, effectiveOptionIdx, JSON.stringify(reviewCopy)]);

  // Leaving a gate (approved elsewhere / regenerating) exits edit mode.
  useEffect(() => {
    if (!isConceptGate && !isCopyGate) setEditing(false);
  }, [isConceptGate, isCopyGate]);

  // Concept-box height sync (Task #1637): report this card's natural concept
  // text height; render the box at the sibling max so all cards line up.
  const conceptSync = useContext(ConceptBoxSyncContext);
  const conceptObserverRef = useRef<ResizeObserver | null>(null);
  const conceptContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      conceptObserverRef.current?.disconnect();
      conceptObserverRef.current = null;
      if (!node || !conceptSync) return;
      const measure = () => conceptSync.report(run.id, Math.ceil(node.offsetHeight));
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      conceptObserverRef.current = ro;
      measure();
    },
    [conceptSync, run.id],
  );
  useEffect(
    () => () => {
      conceptObserverRef.current?.disconnect();
      conceptObserverRef.current = null;
      conceptSync?.report(run.id, null);
    },
    [conceptSync, run.id],
  );

  const [storyboardLightboxOpen, setStoryboardLightboxOpen] = useState(false);
  useEffect(() => {
    if (!storyboardLightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStoryboardLightboxOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [storyboardLightboxOpen]);

  // ── Storyboard gate mutations (Task #1566) ──
  const approveStoryboard = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/approve-storyboard`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Storyboard approved", description: "Generating the slide images now." });
    },
    onError: (e: any) => toast({ title: "Approve failed", description: e?.message || "Could not approve the storyboard", variant: "destructive" }),
  });
  const regenerateStoryboard = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/regenerate-storyboard`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Regenerating storyboard", description: "A fresh storyboard is being created — you'll review it again before images render." });
    },
    onError: (e: any) => toast({ title: "Regenerate failed", description: e?.message || "Could not regenerate the storyboard", variant: "destructive" }),
  });

  // ── Mutations (same endpoints + bodies as the stacked panels) ──
  const approveConcept = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/approve-concept`, {
        ...(editReviewConcept.trim() ? { concept: editReviewConcept.trim() } : {}),
      });
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Concept approved", description: "Writing the slide copy now — you'll review it before any images render." });
    },
    onError: (e: any) => toast({ title: "Approve failed", description: e?.message || "Could not approve the concept", variant: "destructive" }),
  });
  const regenerateConcept = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/regenerate-concept`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Regenerating concept", description: "A fresh concept is being written — you'll review it again before copy is created." });
    },
    onError: (e: any) => toast({ title: "Regenerate failed", description: e?.message || "Could not regenerate the concept", variant: "destructive" }),
  });
  const approveCopy = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/approve-copy`, {
        slideTexts: editSlides.map((t) => t.trim()),
        ...(editConcept.trim() ? { concept: editConcept.trim() } : {}),
        ...(copyOptions.length > 1 ? { selectedOptionIndex: effectiveOptionIdx } : {}),
      });
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Copy approved", description: "Generating the slide images now." });
    },
    onError: (e: any) => toast({ title: "Approve failed", description: e?.message || "Could not approve the copy", variant: "destructive" }),
  });
  const regenerateCopy = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/regenerate-copy`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Regenerating copy", description: "A fresh concept and slide copy are being written — you'll review them again before images are created." });
    },
    onError: (e: any) => toast({ title: "Regenerate failed", description: e?.message || "Could not regenerate the copy", variant: "destructive" }),
  });
  // Task #1566 — retry a FAILED storyboard stage (the run resumes exactly
  // where it failed: prompt re-run if the Sim agent failed, KIE re-render if
  // the render failed).
  const retryStoryboard = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/retry-storyboard`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Retrying storyboard", description: "The storyboard is being generated again." });
    },
    onError: (e: any) => toast({ title: "Retry failed", description: e?.message || "Could not retry the storyboard", variant: "destructive" }),
  });

  const pending =
    approveConcept.isPending || regenerateConcept.isPending || approveCopy.isPending || regenerateCopy.isPending ||
    approveStoryboard.isPending || regenerateStoryboard.isPending || retryStoryboard.isPending;

  const copyValid =
    editSlides.length > 0 &&
    editSlides.every((t) => t.trim().length > 0) &&
    (!reviewCopy.concept || editConcept.trim().length > 0);
  const canApprove = isStoryboardGate
    ? detailRun.status === "awaiting_storyboard_review"
    : isConceptGate
      ? !!editReviewConcept.trim()
      : copyValid;

  const onApprove = () =>
    isStoryboardGate ? approveStoryboard.mutate() : isConceptGate ? approveConcept.mutate() : approveCopy.mutate();
  const onRegenerate = () =>
    isStoryboardGate ? regenerateStoryboard.mutate() : isConceptGate ? regenerateConcept.mutate() : regenerateCopy.mutate();

  // Storyboard gate (Task #1573): the concept + slide text were already
  // approved (edits are written back into copyJson by the approve-copy
  // route), so re-derive them read-only from the persisted approved copy —
  // NOT the copy-gate edit state, which isn't seeded at this gate.
  const approvedReview = isStoryboardGate ? extractCopyReview(detailRun.copyJson) : null;
  const approvedConceptText =
    (approvedReview?.concept || (detailRun.approvedConcept as string | null) || "").trim();
  const approvedSlideTexts = approvedReview?.slides ?? [];

  const conceptText = isStoryboardGate
    ? approvedConceptText
    : isConceptGate
      ? editReviewConcept
      : editConcept;
  const badgeLabel = isStoryboardGate
    ? isStoryboardFailed
      ? "Storyboard failed"
      : "Storyboard awaiting approval"
    : isConceptGate
      ? "Concept awaiting approval"
      : "Copy awaiting approval";

  const copyToClipboard = () => {
    const parts = [conceptText.trim()];
    const clipSlides = isCopyGate ? editSlides : isStoryboardGate ? approvedSlideTexts : [];
    if (clipSlides.length > 0) {
      parts.push(clipSlides.map((s, i) => `${i + 1}. ${s.trim()}`).join("\n"));
    }
    navigator.clipboard?.writeText(parts.filter(Boolean).join("\n\n")).then(
      () => toast({ title: "Concept copied" }),
      () => toast({ title: "Copy failed", variant: "destructive" }),
    );
  };

  // The list can momentarily lag the fresher detail (e.g. right after an
  // approve/regenerate) — show a compact processing state until it catches up.
  const stillAtGate = isConceptGate || isCopyGate || isStoryboardGate;

  return (
    <Card className="flex h-full flex-col border-border/70" data-testid={`card-review-${run.id}`}>
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold" data-testid={`text-review-title-${run.id}`}>
              {run.name || `${styleLabel} — Concept ${conceptNumber}`}
            </p>
            <p className="text-xs text-muted-foreground">{new Date(run.createdAt as any).toLocaleString()}</p>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0 text-muted-foreground"
            onClick={copyToClipboard}
            title="Copy concept text"
            data-testid={`button-copy-review-${run.id}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>

        {!stillAtGate ? (
          <div className="flex flex-1 items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {runStatusMeta(detailRun.status).label}
          </div>
        ) : (
          <>
            <Badge variant="outline" className="w-fit border-primary/40 bg-primary/5 text-primary" data-testid={`badge-review-${run.id}`}>
              {badgeLabel}
            </Badge>

            {/* Multi-option copy gate: pick which option to review/approve */}
            {isCopyGate && copyOptions.length > 1 && (
              <div className="flex flex-wrap gap-1.5" data-testid={`list-review-options-${run.id}`}>
                {copyOptions.map((_, i) => (
                  <Button
                    key={i}
                    type="button"
                    size="sm"
                    variant={effectiveOptionIdx === i ? "default" : "outline"}
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setReviewOptionIdx(i)}
                    disabled={pending}
                    data-testid={`button-review-option-${run.id}-${i}`}
                  >
                    Option {i + 1}
                  </Button>
                ))}
              </div>
            )}

            {/* Concept / angle (all gates; read-only at the storyboard gate —
                already approved, so no textarea there, Task #1573) */}
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Concept / angle</Label>
              {editing ? (
                <Textarea
                  value={conceptText}
                  onChange={(e) => {
                    if (isConceptGate) setEditReviewConcept(e.target.value);
                    else { setEditConcept(e.target.value); setReviewDirty(true); }
                  }}
                  rows={3}
                  className="min-h-0 text-sm"
                  data-testid={`input-review-concept-${run.id}`}
                />
              ) : (
                <div
                  className="overflow-y-auto rounded-md border bg-muted/20 px-3 py-2 text-sm leading-snug"
                  style={{ maxHeight: CONCEPT_BOX_MAX_PX }}
                  data-testid={`text-review-concept-${run.id}`}
                >
                  {/* Sizing layer: stretches to the sibling max so tops/bottoms
                      of every card's concept box align (Task #1637). */}
                  <div style={conceptSync && conceptSync.maxHeight > 0 ? { minHeight: conceptSync.maxHeight } : undefined}>
                    <div ref={conceptContentRef}>
                      {conceptText.trim() ? <ConceptBullets text={conceptText.trim()} /> : <span className="text-muted-foreground">No concept text</span>}
                    </div>
                  </div>
                </div>
              )}
              {editing && !conceptText.trim() && (
                <p className="text-xs text-destructive">The concept needs some text before you can approve.</p>
              )}
            </div>

            {/* On-screen text per slide (copy gate) */}
            {isCopyGate ? (
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">On-screen text per slide</Label>
                <ol className="space-y-1.5">
                  {editSlides.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary", editing ? "mt-2" : "mt-1")}>{i + 1}</span>
                      {editing ? (
                        <Textarea
                          value={s}
                          onChange={(e) => {
                            const next = [...editSlides];
                            next[i] = e.target.value;
                            setEditSlides(next);
                            setReviewDirty(true);
                          }}
                          rows={2}
                          className="min-h-0 text-sm"
                          data-testid={`input-review-slide-${run.id}-${i}`}
                        />
                      ) : (
                        <div className="min-h-[30px] flex-1 rounded-md border bg-background px-2.5 py-1.5 text-sm leading-snug" data-testid={`text-review-slide-${run.id}-${i}`}>
                          {s.trim() || <span className="text-muted-foreground">Empty</span>}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
                {editing && !copyValid && (
                  <p className="text-xs text-destructive">Every slide (and the concept) needs some text before you can approve.</p>
                )}
              </div>
            ) : isStoryboardGate ? (
              /* Single-gate flow: the storyboard gate shows ONLY the concept
                 and the storyboard image — slide text is intentionally not
                 displayed here (user request, July 2026). */
              null
            ) : (
              <p className="flex-1 text-xs text-muted-foreground">
                Slide copy is written after you approve the concept — you'll review it here before any images render.
              </p>
            )}

            {/* Storyboard review (Task #1566 — its own gate AFTER copy
                approval; rendered BELOW the approved concept + slide text,
                Task #1573) */}
            {isStoryboardGate && (
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Storyboard</Label>
                {detailRun.storyboardStatus === "ready" && detailRun.storyboardImageUrl ? (
                  <div className="overflow-hidden rounded-md border bg-muted/20">
                    <img
                      src={detailRun.storyboardImageUrl}
                      alt="Storyboard"
                      className="mx-auto max-h-72 w-auto cursor-zoom-in object-contain transition-opacity hover:opacity-90"
                      loading="lazy"
                      onClick={() => setStoryboardLightboxOpen(true)}
                      data-testid={`img-storyboard-${run.id}`}
                    />
                  </div>
                ) : detailRun.storyboardStatus === "failed" ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2" data-testid={`status-storyboard-failed-${run.id}`}>
                    <p className="min-w-0 text-xs text-destructive">
                      Storyboard failed{detailRun.storyboardError ? ` — ${detailRun.storyboardError}` : ""}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0 px-2.5 text-xs"
                      onClick={() => retryStoryboard.mutate()}
                      disabled={retryStoryboard.isPending}
                      data-testid={`button-retry-storyboard-${run.id}`}
                    >
                      {retryStoryboard.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                      Retry
                    </Button>
                  </div>
                ) : (
                  <div className="flex aspect-[2/3] max-h-72 items-center justify-center rounded-md border bg-muted/20" data-testid={`status-storyboard-loading-${run.id}`}>
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-xs">Rendering storyboard…</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {storyboardLightboxOpen && detailRun.storyboardImageUrl && (
              <div
                className="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-black/80 p-6"
                onClick={() => setStoryboardLightboxOpen(false)}
                data-testid={`lightbox-storyboard-overlay-${run.id}`}
              >
                <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
                  <img
                    src={detailRun.storyboardImageUrl}
                    alt="Storyboard enlarged"
                    className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
                    data-testid={`img-storyboard-lightbox-${run.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => setStoryboardLightboxOpen(false)}
                    className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-black shadow-lg transition-colors hover:bg-white"
                    data-testid={`lightbox-storyboard-close-${run.id}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1">
              {!isStoryboardFailed && (
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={onApprove}
                  disabled={pending || !canApprove}
                  data-testid={`button-approve-review-${run.id}`}
                >
                  {(approveConcept.isPending || approveCopy.isPending || approveStoryboard.isPending) ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Approve
                </Button>
              )}
              {!isStoryboardGate && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing((v) => !v)}
                  disabled={pending}
                  data-testid={`button-edit-review-${run.id}`}
                >
                  {editing ? <Check className="mr-2 h-3.5 w-3.5" /> : <Pencil className="mr-2 h-3.5 w-3.5" />}
                  {editing ? "Done" : "Edit"}
                </Button>
              )}
              {!isStoryboardFailed && (
                <Button
                  size="sm"
                  variant="outline"
                  className="px-2.5"
                  onClick={onRegenerate}
                  disabled={pending}
                  title={isStoryboardGate ? "Regenerate storyboard" : isConceptGate ? "Regenerate concept" : "Regenerate copy"}
                  data-testid={`button-regenerate-review-${run.id}`}
                >
                  {(regenerateConcept.isPending || regenerateCopy.isPending || regenerateStoryboard.isPending) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RunPanel({
  brandId, run, brandName, personaName,
}: {
  brandId: string;
  run: SlideshowExpressRun;
  brandName?: string;
  personaName?: string;
}) {
  const { toast } = useToast();
  const isActive = ACTIVE_RUN_STATUSES.has(run.status);

  // Hardened against the global retry:false + staleTime:Infinity defaults so
  // one failed fetch (e.g. during a server restart) can't freeze this panel on
  // a stale status and hide the review gates. Polls off the FRESH server
  // status (its own latest data), not the possibly-stale parent-list `run`.
  const { data } = useQuery<RunDetail>({
    queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/brands/${brandId}/slideshow-express/runs/${run.id}`);
      return (await res.json()) as RunDetail;
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (query.state.status === "error" || query.state.fetchFailureCount > 0) return 5000;
      const detail = query.state.data as RunDetail | undefined;
      const latest = detail?.run ?? run;
      if (ACTIVE_RUN_STATUSES.has(latest.status)) return 5000;
      // Task #2807 — a per-slide retry re-renders one slide while the run
      // itself stays "failed"; keep polling until that slide settles.
      const slideBusy = (detail?.slides ?? []).some(
        (s) => s.status === "pending" || s.status === "rendering" || s.status === "overlaying",
      );
      return slideBusy ? 5000 : false;
    },
  });
  const slides = data?.slides ?? [];
  const detailRun = data?.run ?? run;

  const handle = handleFromName(brandName || run.name || "brand");
  const meta = runStatusMeta(detailRun.status);

  const retry = useMutation({
    mutationFn: async () => { await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/retry`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
      toast({ title: "Retrying", description: "Regenerating this slideshow." });
    },
    onError: (e: any) => toast({ title: "Retry failed", description: e?.message || "Could not retry", variant: "destructive" }),
  });

  const invalidateRun = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`] });
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
  };

  // ── Concept review gate (FIRST gate, every run) ───────────────────────────
  // Review/edit the Concept Agent's concept before ANY copy is written.
  // Approve sends the (possibly edited) concept and resumes into the Copy
  // Agent (which then parks at the copy gate). Regenerate re-runs ONLY the
  // Concept Agent and parks here again.
  const isAwaitingConceptReview = detailRun.status === "awaiting_concept_review";
  const generatedConcept = extractConceptReviewText(detailRun.conceptJson);
  const [editReviewConcept, setEditReviewConcept] = useState("");
  const conceptSeedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAwaitingConceptReview) {
      conceptSeedRef.current = null;
      return;
    }
    // Reseed on run change or fresh concept after a regenerate; keep edits otherwise.
    const sig = `${run.id}|${generatedConcept}`;
    if (conceptSeedRef.current === sig) return;
    conceptSeedRef.current = sig;
    setEditReviewConcept(generatedConcept);
  }, [isAwaitingConceptReview, run.id, generatedConcept]);
  const approveConcept = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/approve-concept`, {
        ...(editReviewConcept.trim() ? { concept: editReviewConcept.trim() } : {}),
      });
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Concept approved", description: "Writing the slide copy now — you'll review it before any images render." });
    },
    onError: (e: any) => toast({ title: "Approve failed", description: e?.message || "Could not approve the concept", variant: "destructive" }),
  });
  const regenerateConcept = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/regenerate-concept`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Regenerating concept", description: "A fresh concept is being written — you'll review it again before copy is created." });
    },
    onError: (e: any) => toast({ title: "Regenerate failed", description: e?.message || "Could not regenerate the concept", variant: "destructive" }),
  });

  // ── Copy review gate (EVERY run) ──────────────────────────────────────────
  // Approve the generated concept + slide copy: the run resumes automatically
  // (image prompts → rendering → caption). The concept and per-slide text are
  // EDITABLE here — edits are sent with the approval and become the copy that
  // gets rendered. Regenerate re-runs ONLY the Copy Agent and parks again for
  // a fresh review. When the Copy Agent returned multiple options the user can
  // switch between them (switching reseeds the editable fields).
  const isAwaitingReview = detailRun.status === "awaiting_copy_review";
  const copyOptions: any[] = Array.isArray(detailRun.copyOptionsJson) ? (detailRun.copyOptionsJson as any[]) : [];
  const [reviewOptionIdx, setReviewOptionIdx] = useState<number | null>(null);
  const effectiveOptionIdx = reviewOptionIdx ?? (detailRun.selectedCopyOption ?? 0);
  const reviewSource =
    copyOptions.length > 1 && copyOptions[effectiveOptionIdx]
      ? copyOptions[effectiveOptionIdx]
      : detailRun.copyJson;
  const reviewCopy = extractCopyReview(reviewSource);
  const [editConcept, setEditConcept] = useState("");
  const [editSlides, setEditSlides] = useState<string[]>([]);
  const [reviewDirty, setReviewDirty] = useState(false);
  // Seed (and reseed) the editable fields from the displayed option's copy.
  // Reseeds on run change, option switch, or fresh copy after a regenerate —
  // but never clobbers in-progress edits for the SAME run+option.
  const seedSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAwaitingReview) {
      seedSigRef.current = null;
      setReviewDirty(false);
      setReviewOptionIdx(null);
      return;
    }
    const sig = `${run.id}|${effectiveOptionIdx}|${JSON.stringify(reviewCopy)}`;
    if (seedSigRef.current === sig) return;
    const optionOrRunChanged =
      !seedSigRef.current || !seedSigRef.current.startsWith(`${run.id}|${effectiveOptionIdx}|`);
    if (reviewDirty && !optionOrRunChanged) return; // keep the user's edits
    seedSigRef.current = sig;
    setEditConcept(reviewCopy.concept);
    setEditSlides(reviewCopy.slides);
    setReviewDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAwaitingReview, run.id, effectiveOptionIdx, JSON.stringify(reviewCopy)]);
  const reviewValid =
    editSlides.length > 0 &&
    editSlides.every((t) => t.trim().length > 0) &&
    (!reviewCopy.concept || editConcept.trim().length > 0);
  const approveCopy = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/approve-copy`, {
        slideTexts: editSlides.map((t) => t.trim()),
        ...(editConcept.trim() ? { concept: editConcept.trim() } : {}),
        ...(copyOptions.length > 1 ? { selectedOptionIndex: effectiveOptionIdx } : {}),
      });
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Copy approved", description: "Generating the slide images now." });
    },
    onError: (e: any) => toast({ title: "Approve failed", description: e?.message || "Could not approve the copy", variant: "destructive" }),
  });
  const regenerateCopy = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/regenerate-copy`, {});
    },
    onSuccess: () => {
      invalidateRun();
      toast({ title: "Regenerating copy", description: "A fresh concept and slide copy are being written — you'll review them again before images are created." });
    },
    onError: (e: any) => toast({ title: "Regenerate failed", description: e?.message || "Could not regenerate the copy", variant: "destructive" }),
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteRun = useMutation({
    mutationFn: async () => { await apiRequest("DELETE", `/api/brands/${brandId}/slideshow-express/runs/${run.id}`); },
    onSuccess: () => {
      setConfirmDelete(false);
      // Drop this run's detail cache entirely (the panel unmounts) and refresh
      // the list so the deleted run disappears immediately.
      queryClient.removeQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`], exact: true });
      toast({ title: "Slideshow deleted", description: "The run and its slides were removed. Saved library ads are untouched." });
    },
    onError: (e: any) => {
      setConfirmDelete(false);
      toast({ title: "Could not delete", description: e?.message || "Failed to delete run", variant: "destructive" });
    },
  });

  const copyText = (label: string, text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast({ title: `${label} copied` }),
      () => toast({ title: "Copy failed", variant: "destructive" }),
    );
  };

  // ── Save to My Ads ────────────────────────────────────────────────────────
  const invalidateAfterSave = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
    // Refresh every My Ads listing variation for this brand.
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        (q.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
    });
  };

  const [savingSlideId, setSavingSlideId] = useState<string | null>(null);
  const saveSlide = useMutation({
    mutationFn: async (slideId: string) => {
      setSavingSlideId(slideId);
      const res = await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/slides/${slideId}/save`);
      return (await res.json()) as { generatedAdId: string; alreadySaved: boolean };
    },
    onSuccess: (data) => {
      invalidateAfterSave();
      toast({
        title: data.alreadySaved ? "Already in My Ads" : "Saved to My Ads",
        description: data.alreadySaved
          ? "This slideshow was saved earlier."
          : "The whole slideshow was added to your Ad Library as one entry.",
      });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message || "Could not save slide", variant: "destructive" }),
    onSettled: () => setSavingSlideId(null),
  });

  const saveAll = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/slideshow-express/runs/${run.id}/save-all`);
      return (await res.json()) as { saved: number; alreadySaved: number; failed: number; errors: string[] };
    },
    onSuccess: (data) => {
      invalidateAfterSave();
      if (data.failed > 0) {
        toast({
          title: `Saved ${data.saved} slide${data.saved === 1 ? "" : "s"}, ${data.failed} failed`,
          description: data.errors.slice(0, 2).join(" · "),
          variant: "destructive",
        });
      } else if (data.saved === 0 && data.alreadySaved > 0) {
        toast({ title: "Already in My Ads", description: "This slideshow was saved earlier." });
      } else {
        toast({
          title: "Saved slideshow to My Ads",
          description: data.alreadySaved > 0
            ? `${data.saved} new slide${data.saved === 1 ? "" : "s"} added to the saved slideshow.`
            : "The whole slideshow was added to your Ad Library as one entry.",
        });
      }
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message || "Could not save slides", variant: "destructive" }),
  });

  const readySlides = slides.filter((s) => s.status === "ready" && s.compositedImageUrl);
  const allSaved = readySlides.length > 0 && readySlides.every((s) => s.savedGeneratedAdId);

  const hashtags = Array.isArray(detailRun.hashtags) ? detailRun.hashtags : [];

  return (
    <Card data-testid={`panel-run-${run.id}`}>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Images className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium" data-testid={`text-run-name-${run.id}`}>{run.name || "Slideshow run"}</span>
          {personaName && (
            <Badge variant="outline" className="gap-1 font-normal" data-testid={`badge-persona-${run.id}`}>
              <Users className="h-3 w-3" />
              {personaName}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">{new Date(run.createdAt as any).toLocaleString()}</span>
          <Badge variant={meta.variant} className="ml-auto gap-1" data-testid={`badge-status-${run.id}`}>
            {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
            {meta.label}
          </Badge>
          {detailRun.status === "ready" && readySlides.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => saveAll.mutate()}
              disabled={saveAll.isPending || allSaved}
              data-testid={`button-save-all-${run.id}`}
            >
              {saveAll.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : allSaved ? (
                <Check className="mr-2 h-4 w-4" />
              ) : (
                <Bookmark className="mr-2 h-4 w-4" />
              )}
              {allSaved ? "Saved to My Ads" : "Save slideshow to My Ads"}
            </Button>
          )}
          {detailRun.status === "ready" && (
            <a href={`/api/brands/${brandId}/slideshow-express/runs/${run.id}/export`} data-testid={`link-export-${run.id}`}>
              <Button size="sm" variant="outline"><Download className="mr-2 h-4 w-4" /> Export .zip</Button>
            </a>
          )}
          {detailRun.status === "failed" && (
            <Button size="sm" variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending} data-testid={`button-retry-${run.id}`}>
              {retry.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Retry
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteRun.isPending || isActive}
            title={isActive ? "Wait for generation to finish before deleting" : "Delete this slideshow"}
            data-testid={`button-delete-run-${run.id}`}
          >
            {deleteRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={(open) => { if (!deleteRun.isPending) setConfirmDelete(open); }}>
          <AlertDialogContent data-testid={`dialog-delete-run-${run.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this slideshow?</AlertDialogTitle>
              <AlertDialogDescription>
                "{run.name || "Slideshow run"}" and its slides will be permanently removed. Slideshows already saved to My Ads keep their own copies and are not affected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteRun.isPending} data-testid={`button-cancel-delete-${run.id}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); deleteRun.mutate(); }}
                disabled={deleteRun.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid={`button-confirm-delete-${run.id}`}
              >
                {deleteRun.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* User-provided creative controls snapshotted on the run */}
        {(detailRun.approvedConcept || detailRun.customConcept || detailRun.ctaText) && (
          <div className="space-y-1.5 rounded-md border bg-muted/30 p-3 text-sm">
            {/* Once copy is approved, show the concept that was actually
                approved (possibly user-edited) instead of the free-form
                direction the user typed at kickoff. */}
            {detailRun.approvedConcept ? (
              <div className="flex items-start gap-2" data-testid={`text-run-concept-${run.id}`}>
                <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0"><span className="font-medium">Approved concept:</span> <div className="text-muted-foreground"><ConceptBullets text={detailRun.approvedConcept} /></div></div>
              </div>
            ) : detailRun.customConcept ? (
              <div className="flex items-start gap-2" data-testid={`text-run-concept-${run.id}`}>
                <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0"><span className="font-medium">Concept direction:</span> <div className="text-muted-foreground"><ConceptBullets text={detailRun.customConcept} /></div></div>
              </div>
            ) : null}
            {detailRun.ctaText && (
              <div className="flex items-start gap-2" data-testid={`text-run-cta-${run.id}`}>
                <Megaphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span><span className="font-medium">Last-slide CTA:</span> <span className="text-muted-foreground">{detailRun.ctaText}</span></span>
              </div>
            )}
          </div>
        )}

        {detailRun.status === "failed" && detailRun.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <span className="line-clamp-3">{detailRun.error}</span>
          </div>
        )}

        {/* Copy review gate — EVERY run parks here after the Copy Agent
            finishes. The user reviews (and can edit) the generated concept +
            per-slide text, then approves (the run resumes automatically with
            the edited copy) or regenerates the copy for a fresh review. */}
        {isAwaitingConceptReview && (
          <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid={`panel-concept-review-${run.id}`}>
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Review the concept</p>
                <p className="text-xs text-muted-foreground">
                  Tweak the concept if you like, then approve to write the slide copy (which you'll also review) — or regenerate for a different take.
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Concept</Label>
              <Textarea
                value={editReviewConcept}
                onChange={(e) => setEditReviewConcept(e.target.value)}
                rows={4}
                className="min-h-0 text-sm"
                data-testid={`input-concept-review-${run.id}`}
              />
              {!editReviewConcept.trim() && (
                <p className="text-xs text-destructive">The concept needs some text before you can approve.</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => approveConcept.mutate()}
                disabled={approveConcept.isPending || regenerateConcept.isPending || !editReviewConcept.trim()}
                data-testid={`button-approve-concept-${run.id}`}
              >
                {approveConcept.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                Approve &amp; continue
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => regenerateConcept.mutate()}
                disabled={approveConcept.isPending || regenerateConcept.isPending}
                data-testid={`button-regenerate-concept-${run.id}`}
              >
                {regenerateConcept.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Regenerate concept
              </Button>
            </div>
          </div>
        )}

        {isAwaitingReview && (
          <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid={`panel-copy-review-${run.id}`}>
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Review the generated copy</p>
                <p className="text-xs text-muted-foreground">
                  Tweak the concept or any slide's text if you like, then approve to render the slide images — or regenerate for a different take.
                </p>
              </div>
            </div>
            {copyOptions.length > 1 && (
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Copy options</Label>
                <div className="flex flex-wrap gap-1.5" data-testid={`list-review-options-${run.id}`}>
                  {copyOptions.map((_, i) => (
                    <Button
                      key={i}
                      type="button"
                      size="sm"
                      variant={effectiveOptionIdx === i ? "default" : "outline"}
                      onClick={() => setReviewOptionIdx(i)}
                      disabled={approveCopy.isPending || regenerateCopy.isPending}
                      data-testid={`button-review-option-${run.id}-${i}`}
                    >
                      Option {i + 1}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {(reviewCopy.concept || editConcept) && (
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Concept / angle</Label>
                <Textarea
                  value={editConcept}
                  onChange={(e) => { setEditConcept(e.target.value); setReviewDirty(true); }}
                  rows={2}
                  className="min-h-0 text-sm"
                  data-testid={`input-review-gate-concept-${run.id}`}
                />
              </div>
            )}
            {editSlides.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">On-screen text per slide</Label>
                <ol className="space-y-1.5">
                  {editSlides.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">{i + 1}</span>
                      <Textarea
                        value={s}
                        onChange={(e) => {
                          const next = [...editSlides];
                          next[i] = e.target.value;
                          setEditSlides(next);
                          setReviewDirty(true);
                        }}
                        rows={2}
                        className="min-h-0 text-sm"
                        data-testid={`input-review-gate-slide-${run.id}-${i}`}
                      />
                    </li>
                  ))}
                </ol>
                {!reviewValid && (
                  <p className="text-xs text-destructive">Every slide (and the concept) needs some text before you can approve.</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => approveCopy.mutate()}
                disabled={approveCopy.isPending || regenerateCopy.isPending || !reviewValid}
                data-testid={`button-approve-copy-${run.id}`}
              >
                {approveCopy.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                Approve &amp; continue
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => regenerateCopy.mutate()}
                disabled={approveCopy.isPending || regenerateCopy.isPending}
                data-testid={`button-regenerate-copy-${run.id}`}
              >
                {regenerateCopy.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Regenerate copy
              </Button>
            </div>
          </div>
        )}

        {/* Final post — TikTok-style phone mock */}
        {slides.length > 0 ? (
          <TikTokPost
            slides={slides}
            run={detailRun}
            handle={handle}
            caption={detailRun.caption || ""}
            hashtags={hashtags}
            onSaveSlide={(slideId) => saveSlide.mutate(slideId)}
            savingSlideId={savingSlideId}
          />
        ) : isActive ? (
          <div className="flex justify-center">
            <Skeleton className="aspect-[9/19.5] w-full max-w-[300px] rounded-[3rem]" />
          </div>
        ) : null}

        {/* Final package */}
        {detailRun.status === "ready" && (
          <div className="grid gap-3 md:grid-cols-3">
            <PackageBlock icon={MessageSquare} title="Caption" value={detailRun.caption || ""} onCopy={() => copyText("Caption", detailRun.caption || "")} testid={`text-caption-${run.id}`} />
            <PackageBlock icon={Hash} title="Hashtags" value={hashtags.join(" ")} onCopy={() => copyText("Hashtags", hashtags.join(" "))} testid={`text-hashtags-${run.id}`} />
            <PackageBlock icon={MessageSquare} title="Pinned comment" value={detailRun.pinnedComment || ""} onCopy={() => copyText("Pinned comment", detailRun.pinnedComment || "")} testid={`text-pinned-${run.id}`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Full TikTok-style phone mock: swipeable 4:5 slides are centered in the screen
// the real TikTok chrome overlaid — status bar, For You tabs, right-hand action
// rail (likes / comments / saves / share), the @handle + caption + hashtags,
// the sound row, and the bottom tab bar — exactly like an organic TikTok post.
function TikTokPost({
  slides, run, handle, caption, hashtags, onSaveSlide, savingSlideId,
}: {
  slides: SlideshowExpressSlide[];
  run: SlideshowExpressRun;
  handle: string;
  caption: string;
  hashtags: string[];
  onSaveSlide: (slideId: string) => void;
  savingSlideId: string | null;
}) {
  const { toast } = useToast();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [editingSlideId, setEditingSlideId] = useState<string | null>(null);
  const editingSlide = slides.find((s) => s.id === editingSlideId) || null;
  // Drag-to-place / corner-resize: grabbing the overlaid text on a ready slide
  // immediately swaps in the clean (text-free) image with a client-rendered
  // text block; releasing the gesture auto-saves (server re-composites at the
  // new spot/size). Live per-frame positions stay INSIDE the overlay layer
  // (direct DOM transforms) — the parent only learns about start/end/cancel,
  // so the whole phone mock never re-renders mid-gesture.
  const [movingSlideId, setMovingSlideId] = useState<string | null>(null);
  const count = slides.length;
  // The run's style may be admin-created with Manage Styles text settings
  // (font/size/placement/color) — the drag overlay needs them so its hit
  // region and preview sit exactly where the server composited the text.
  // Shares the page-level query (same key + relaxed retry, see above).
  const { data: allStyles } = useQuery<SlideshowExpressStyleRecord[]>({
    queryKey: ["/api/slideshow-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
  });
  const styleSettings = useMemo(
    () => parseSlideshowTextSettings(allStyles?.find((s) => s.value === run.styleId)?.textSettings) ?? null,
    [allStyles, run.styleId],
  );
  const m = useMemo(() => deriveMetrics(run.id), [run.id]);
  const tags = hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`));

  // Leaving the slide (swipe/arrows) exits move mode — the controls below the
  // phone always describe the ACTIVE slide.
  useEffect(() => {
    if (movingSlideId && slides[active]?.id !== movingSlideId) {
      setMovingSlideId(null);
    }
  }, [active, movingSlideId, slides]);

  const savePosition = useMutation({
    mutationFn: async ({ slide, pos, sizePx }: { slide: SlideshowExpressSlide; pos: { x: number; y: number } | null; sizePx?: number }) => {
      // Merge into the slide's existing overrides so font/color/size/align
      // edits survive; null position = reset back to the preset placement.
      // A sizePx from a corner-resize gesture merges in the same way.
      const existing = parseSlideOverlayOverrides(slide.overlayJson) || {};
      const next: SlideOverlayOverrides = { ...existing };
      if (pos) {
        next.posX = Math.round(pos.x * 10000) / 10000;
        next.posY = Math.round(pos.y * 10000) / 10000;
      } else {
        delete next.posX;
        delete next.posY;
      }
      if (sizePx != null) {
        next.sizePx = Math.min(160, Math.max(16, Math.round(sizePx)));
      }
      await apiRequest(
        "PATCH",
        `/api/brands/${run.brandId}/slideshow-express/runs/${run.id}/slides/${slide.id}/overlay`,
        { overrides: Object.keys(next).length > 0 ? next : null },
      );
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${run.brandId}/slideshow-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${run.brandId}/slideshow-express/runs`, run.id] });
      toast({
        title: !vars.pos ? "Position reset" : vars.sizePx != null ? "Text resized" : "Text moved",
        description: vars.pos
          ? `The slide was re-rendered with the text at its new ${vars.sizePx != null ? "size" : "spot"}.`
          : "The text is back at its default placement.",
      });
      setMovingSlideId(null);
    },
    onError: (e: any) => {
      // Restore the prior look: dropping move mode reveals the existing
      // composited image (text at its old spot/size).
      setMovingSlideId(null);
      toast({ title: "Update failed", description: e?.message || "Could not update the slide text", variant: "destructive" });
    },
  });

  const scrollToIndex = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(count - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="carousel-slides">
      {/* Realistic iPhone: titanium frame, thin bezel, Dynamic Island, side buttons */}
      <div className="relative w-full max-w-[300px]">
        {/* Side buttons */}
        <div className="absolute -left-[2px] top-[15%] h-8 w-[3px] rounded-l-sm bg-neutral-700" />
        <div className="absolute -left-[2px] top-[27%] h-14 w-[3px] rounded-l-sm bg-neutral-700" />
        <div className="absolute -left-[2px] top-[41%] h-14 w-[3px] rounded-l-sm bg-neutral-700" />
        <div className="absolute -right-[2px] top-[31%] h-20 w-[3px] rounded-r-sm bg-neutral-700" />
        {/* Titanium frame */}
        <div className="relative aspect-[9/19.5] w-full rounded-[3rem] bg-gradient-to-br from-neutral-600 via-neutral-800 to-neutral-950 p-[11px] shadow-2xl ring-1 ring-black/40">
          {/* Screen */}
          <div className="relative h-full w-full overflow-hidden rounded-[2.2rem] bg-black">
        {/* Image carousel fills the phone screen */}
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="absolute inset-0 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-contain touch-pan-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="carousel-scroller"
        >
          {slides.map((slide) => (
            <div key={slide.id} className="h-full w-full shrink-0 snap-center" data-testid={`card-slide-${slide.id}`}>
              <SlideFrame
                slide={slide}
                run={run}
                settings={styleSettings}
                moving={movingSlideId === slide.id}
                onGestureStart={() => setMovingSlideId(slide.id)}
                onGestureEnd={({ pos, sizePx }) => {
                  if (movingSlideId === slide.id) {
                    savePosition.mutate({ slide, pos, sizePx });
                  } else {
                    setMovingSlideId(null);
                  }
                }}
                onGestureCancel={() => setMovingSlideId(null)}
                savingPosition={savePosition.isPending && movingSlideId === slide.id}
              />
            </div>
          ))}
        </div>

        {/* Legibility gradients */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />

        {/* Dynamic Island */}
        <div className="pointer-events-none absolute left-1/2 top-[9px] z-30 h-[26px] w-[88px] -translate-x-1/2 rounded-full bg-black" />

        {/* Status bar */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 pt-[13px] text-[11px] font-semibold text-white">
          <span>12:09</span>
          <div className="flex items-center gap-1">
            <SignalHigh className="h-3 w-3" /><Wifi className="h-3 w-3" /><BatteryFull className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* Top tabs (Following / For You) + LIVE + search */}
        <div className="pointer-events-none absolute inset-x-0 top-[44px] flex items-center px-3 text-white">
          <span className="text-[10px] font-bold tracking-wide">LIVE</span>
          <div className="mx-auto flex items-center gap-4 text-[13px]">
            <span className="text-white/60">Following</span>
            <span className="relative font-semibold">
              For You
              <span className="absolute -bottom-1 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-white" />
            </span>
          </div>
          <Search className="h-4 w-4" />
        </div>

        {/* Photo-mode slide counter */}
        {count > 1 && (
          <div className="pointer-events-none absolute right-3 top-[72px] rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white" data-testid="text-slide-count">
            {active + 1}/{count}
          </div>
        )}

        {/* Right action rail */}
        <div className="pointer-events-none absolute bottom-14 right-2 flex flex-col items-center gap-4 text-white">
          <div className="relative mb-1">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-neutral-700">
              <User className="h-6 w-6" />
            </div>
            <span className="absolute -bottom-2 left-1/2 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full bg-[#fe2c55]">
              <Plus className="h-3 w-3" strokeWidth={3} />
            </span>
          </div>
          <RailStat icon={<Heart className="h-7 w-7 fill-white" />} label={formatCount(m.likes)} testid="stat-likes" />
          <RailStat icon={<MessageCircle className="h-7 w-7" />} label={formatCount(m.comments)} testid="stat-comments" />
          <RailStat icon={<Bookmark className="h-7 w-7" />} label={formatCount(m.saves)} testid="stat-saves" />
          <RailStat icon={<Share2 className="h-7 w-7" />} label="Share" testid="stat-share" />
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-neutral-600 bg-neutral-900 animate-[spin_4s_linear_infinite]">
            <Music2 className="h-4 w-4" />
          </div>
        </div>

        {/* Bottom-left: handle, caption + hashtags, sound row */}
        <div className="pointer-events-none absolute bottom-12 left-3 right-16 space-y-1.5 text-white">
          <span className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2 py-0.5 text-[11px] font-medium backdrop-blur-sm">
            <Repeat2 className="h-3 w-3" /> Repost
          </span>
          <div className="text-sm font-semibold" data-testid="text-tt-handle">@{handle}</div>
          {(caption || tags.length > 0) && (
            <p className="text-[13px] leading-snug line-clamp-4" data-testid="text-tt-caption">
              {caption}
              {tags.length > 0 && (
                <>{caption ? " " : ""}<span className="font-medium">{tags.join(" ")}</span></>
              )}
            </p>
          )}
          <div className="flex items-center gap-1.5 text-xs">
            <Music2 className="h-3 w-3 shrink-0" />
            <span className="truncate">original sound - {handle}</span>
          </div>
        </div>

        {/* Bottom tab bar */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-around bg-black/55 px-2 pb-1.5 pt-1 text-[9px] text-white/85 backdrop-blur-sm">
          <NavItem icon={<Home className="h-5 w-5" />} label="Home" />
          <NavItem icon={<Users className="h-5 w-5" />} label="Friends" />
          <div className="flex h-6 w-9 items-center justify-center rounded-md bg-white text-black"><Plus className="h-4 w-4" strokeWidth={3} /></div>
          <NavItem icon={<MessageSquare className="h-5 w-5" />} label="Inbox" />
          <NavItem icon={<User className="h-5 w-5" />} label="Profile" />
            </div>
          </div>
        </div>
      </div>

      {/* Carousel controls (below the mock so they don't clutter the post) */}
      {count > 1 && (
        <div className="flex items-center gap-3">
          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} data-testid="button-slide-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5" data-testid="carousel-dots">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollToIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === active ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50",
                )}
                data-testid={`dot-slide-${i}`}
              />
            ))}
          </div>
          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => scrollToIndex(active + 1)} disabled={active === count - 1} data-testid="button-slide-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Per-slide actions: edit the overlaid text + save the WHOLE slideshow
          into the My Ads library as one entry */}
      {(() => {
        const slide = slides[Math.max(0, Math.min(count - 1, active))];
        if (!slide || slide.status !== "ready" || !slide.compositedImageUrl) return null;
        // The whole run is saved as ONE library entry — "saved" means every
        // ready slide is already stamped into it.
        const readyHere = slides.filter((s) => s.status === "ready" && s.compositedImageUrl);
        const saved = readyHere.length > 0 && readyHere.every((s) => s.savedGeneratedAdId);
        const saving = savingSlideId === slide.id;
        const draggableText = !!slide.rawImageUrl && !!(slide.slideText || "").trim();
        const hasCustomPos = hasOverlayPosition(parseSlideOverlayOverrides(slide.overlayJson));
        return (
          <div className="flex flex-col items-center gap-2">
            {draggableText && (
              <p className="text-xs text-muted-foreground" data-testid={`text-drag-hint-${slide.id}`}>
                Drag the text to move it, or tap it and pull a corner to resize — it saves automatically
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
            {slide.rawImageUrl && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingSlideId(slide.id)}
                data-testid={`button-edit-slide-${slide.id}`}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit text
              </Button>
            )}
            {draggableText && hasCustomPos && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => savePosition.mutate({ slide, pos: null })}
                disabled={savePosition.isPending}
                data-testid={`button-reset-position-${slide.id}`}
              >
                {savePosition.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Reset position
              </Button>
            )}
            <Button
              size="sm"
              variant={saved ? "secondary" : "outline"}
              onClick={() => onSaveSlide(slide.id)}
              disabled={saving || saved}
              data-testid={`button-save-slide-${slide.id}`}
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="mr-2 h-4 w-4" />
              ) : (
                <Bookmark className="mr-2 h-4 w-4" />
              )}
              {saved ? "Saved to My Ads" : "Save slideshow to My Ads"}
            </Button>
            </div>
          </div>
        );
      })()}

      {/* Slide text editor dialog (keyed by slide so state re-seeds per slide) */}
      {editingSlide && (
        <SlideTextEditor
          key={editingSlide.id}
          run={run}
          slide={editingSlide}
          onClose={() => setEditingSlideId(null)}
        />
      )}
    </div>
  );
}

// ── Slide text editor ─────────────────────────────────────────────────────────
// Edits the text overlaid on a rendered slide: the copy itself plus font,
// color, size, horizontal alignment and vertical position. Saving re-composites
// the slide server-side from its persisted CLEAN image (no AI re-render), so
// edits are fast and free. "Reset styling" returns to the visual style's
// default look while keeping the edited text.
function SlideTextEditor({
  run, slide, onClose,
}: {
  run: SlideshowExpressRun;
  slide: SlideshowExpressSlide;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const brandId = run.brandId;

  const styleDefault = useMemo(() => resolveSlideTextStyle(run.styleId), [run.styleId]);
  const styleFontKey = overlayFontKeyForFamily(styleDefault.fontFamily);
  const styleFontLabel =
    SLIDESHOW_OVERLAY_FONTS.find((f) => f.key === styleFontKey)?.label.split(" — ")[0] || "style font";
  const existing = useMemo(() => parseSlideOverlayOverrides(slide.overlayJson), [slide.overlayJson]);
  const hasDragPosition = hasOverlayPosition(existing);

  const [text, setText] = useState(slide.slideText || "");
  const [fontKey, setFontKey] = useState<string>(existing?.fontKey || "__style");
  const [color, setColor] = useState<string>(existing?.color || styleDefault.color);
  const [sizePx, setSizePx] = useState<number>(existing?.sizePx || styleDefault.sizePx);
  const [align, setAlign] = useState<"left" | "center" | "right">(existing?.align || "center");
  const [vertical, setVertical] = useState<"top" | "middle" | "bottom">(existing?.vertical || "middle");
  // A drag-placed position (set via "Move text") is carried through style/text
  // edits unless the user explicitly resets it here.
  const [keepPosition, setKeepPosition] = useState(true);

  const resetStyling = () => {
    setFontKey("__style");
    setColor(styleDefault.color);
    setSizePx(styleDefault.sizePx);
    setAlign("center");
    setVertical("middle");
  };

  const save = useMutation({
    mutationFn: async () => {
      // Only fields that DIFFER from the style default become overrides, so a
      // control left at its default keeps following the visual style.
      const overrides: SlideOverlayOverrides = {};
      if (fontKey !== "__style") overrides.fontKey = fontKey;
      if (color.toLowerCase() !== styleDefault.color.toLowerCase()) overrides.color = color.toLowerCase();
      if (sizePx !== styleDefault.sizePx) overrides.sizePx = sizePx;
      if (align !== "center") overrides.align = align;
      if (vertical !== "middle") overrides.vertical = vertical;
      if (hasDragPosition && keepPosition && existing) {
        overrides.posX = existing.posX;
        overrides.posY = existing.posY;
      }
      // Color is the ONE styling control that propagates run-wide: when the
      // user changed it (including a reset back to the style default), the
      // server applies it to EVERY slide of the run. Text/font/size/alignment/
      // position edits stay per-slide.
      const currentEffectiveColor = (existing?.color || styleDefault.color).toLowerCase();
      const colorChanged = color.toLowerCase() !== currentEffectiveColor;
      await apiRequest(
        "PATCH",
        `/api/brands/${brandId}/slideshow-express/runs/${run.id}/slides/${slide.id}/overlay`,
        {
          text: text.trim(),
          overrides: Object.keys(overrides).length > 0 ? overrides : null,
          ...(colorChanged ? { applyColorToAllSlides: true } : {}),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/slideshow-express/runs`, run.id] });
      toast({ title: "Slide updated", description: "The text was re-applied to the slide." });
      onClose();
    },
    onError: (e: any) =>
      toast({ title: "Update failed", description: e?.message || "Could not update the slide text", variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !save.isPending) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="dialog-edit-slide-text">
        <DialogHeader>
          <DialogTitle>Edit slide text</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="slide-text">Text</Label>
            <Textarea
              id="slide-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="On-screen slide text"
              data-testid="input-edit-slide-text"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Font</Label>
            <Select value={fontKey} onValueChange={setFontKey}>
              <SelectTrigger data-testid="select-edit-font">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__style">Style default ({styleFontLabel})</SelectItem>
                {SLIDESHOW_OVERLAY_FONTS.map((f) => (
                  <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="slide-color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  id="slide-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded-md border bg-background p-1"
                  data-testid="input-edit-color"
                />
                <span className="text-xs uppercase text-muted-foreground" data-testid="text-edit-color-hex">{color}</span>
              </div>
              <p className="text-xs text-muted-foreground">Applies to all slides</p>
            </div>
            <div className="space-y-1.5">
              <Label>Size <span className="text-muted-foreground">({sizePx}px)</span></Label>
              <Slider
                value={[sizePx]}
                onValueChange={([v]) => setSizePx(v)}
                min={16}
                max={160}
                step={1}
                className="pt-2"
                data-testid="slider-edit-size"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Alignment</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={align}
                onValueChange={(v) => { if (v) setAlign(v as typeof align); }}
                className="justify-start"
              >
                <ToggleGroupItem value="left" aria-label="Align left" data-testid="toggle-align-left"><AlignLeft className="h-4 w-4" /></ToggleGroupItem>
                <ToggleGroupItem value="center" aria-label="Align center" data-testid="toggle-align-center"><AlignCenter className="h-4 w-4" /></ToggleGroupItem>
                <ToggleGroupItem value="right" aria-label="Align right" data-testid="toggle-align-right"><AlignRight className="h-4 w-4" /></ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="space-y-1.5">
              <Label>Position</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={vertical}
                onValueChange={(v) => { if (v) setVertical(v as typeof vertical); }}
                className="justify-start"
              >
                <ToggleGroupItem value="top" aria-label="Position top" data-testid="toggle-vertical-top"><ArrowUpToLine className="h-4 w-4" /></ToggleGroupItem>
                <ToggleGroupItem value="middle" aria-label="Position middle" data-testid="toggle-vertical-middle"><FoldVertical className="h-4 w-4" /></ToggleGroupItem>
                <ToggleGroupItem value="bottom" aria-label="Position bottom" data-testid="toggle-vertical-bottom"><ArrowDownToLine className="h-4 w-4" /></ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {hasDragPosition && keepPosition && (
            <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                This text was dragged to a custom spot, which overrides the position preset above.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => setKeepPosition(false)}
                data-testid="button-reset-drag-position"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset position
              </Button>
            </div>
          )}
          {hasDragPosition && !keepPosition && (
            <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                The custom position will be cleared when you apply.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => setKeepPosition(true)}
                data-testid="button-keep-drag-position"
              >
                Keep position
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" size="sm" onClick={resetStyling} disabled={save.isPending} data-testid="button-reset-overlay">
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset styling
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending} data-testid="button-cancel-overlay">
              Cancel
            </Button>
            <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !text.trim()} data-testid="button-save-overlay">
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              {save.isPending ? "Applying…" : "Apply"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RailStat({ icon, label, testid }: { icon: ReactNode; label: string; testid: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={testid}>
      {icon}
      <span className="text-[11px] font-semibold">{label}</span>
    </div>
  );
}

function NavItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      {icon}
      <span>{label}</span>
    </div>
  );
}

// ── Drag-to-place helpers ─────────────────────────────────────────────────────
// The slide image is 1080×1350; positions are normalized fractions of it. When
// no drag position exists yet, seed the draggable block at the same spot the
// server's preset placement math would put it — including the style's
// admin-saved Manage Styles settings (shared mergeSlideTextSettings +
// resolveOverlayBlockTop), so the hit region always sits on the baked text.
const SLIDE_IMG_W = SLIDESHOW_EXPRESS_CANVAS.width;
const SLIDE_IMG_H = SLIDESHOW_EXPRESS_CANVAS.height;
type SlideCanvas = { width: number; height: number };
const DEFAULT_SLIDE_CANVAS: SlideCanvas = { width: SLIDE_IMG_W, height: SLIDE_IMG_H };

export function overlayPreviewModel(
  slide: SlideshowExpressSlide,
  run: SlideshowExpressRun,
  settings?: SlideshowTextSettings | null,
  sizePxOverride?: number,
  canvas: SlideCanvas = DEFAULT_SLIDE_CANVAS,
) {
  const existing = parseSlideOverlayOverrides(slide.overlayJson);
  // Layers 1+2 exactly like the server compositor: the style's hardcoded
  // defaults merged with the style's admin-saved Manage Styles settings
  // (admin-created styles fall back to the default look otherwise, which put
  // the hit region somewhere the baked text isn't).
  const style = mergeSlideTextSettings(resolveSlideTextStyle(run.styleId), settings);
  const align = existing?.align || style.align || "center";
  const font = existing?.fontKey ? findOverlayFont(existing.fontKey) : undefined;
  const layout = layoutSlideText({
    text: cleanOverlayText(slide.slideText || ""),
    W: canvas.width,
    sizePx: sizePxOverride ?? (existing?.sizePx || style.sizePx),
    lineHeight: style.lineHeight,
    letterSpacingEm: style.letterSpacingEm,
    avgAdvance: font?.avgAdvance ?? style.avgAdvance,
    align,
  });
  return {
    existing,
    style,
    align,
    layout,
    fontFamily: font?.family || style.fontFamily,
    fontWeight: font?.fontWeight ?? style.fontWeight,
    color: existing?.color || style.color,
  };
}

export function initialOverlayPos(
  slide: SlideshowExpressSlide,
  run: SlideshowExpressRun,
  settings?: SlideshowTextSettings | null,
  canvas: SlideCanvas = DEFAULT_SLIDE_CANVAS,
): { x: number; y: number } {
  const { existing, align, layout, style } = overlayPreviewModel(slide, run, settings, undefined, canvas);
  if (hasOverlayPosition(existing)) return { x: existing.posX, y: existing.posY };
  const H = canvas.height;
  // Same preset math the server composites with (user vertical override wins,
  // then an admin-explicit placement, else the legacy 43% center).
  const top = resolveOverlayBlockTop({
    vertical: existing?.vertical,
    placement: style.placement,
    placementExplicit: style.placementExplicit,
    H,
    totalHeight: layout.totalHeight,
  });
  const x = align === "left" ? 0.08 : align === "right" ? 0.92 : 0.5;
  return { x, y: (top + layout.totalHeight / 2) / H };
}

// Min/max font size for the corner-resize gesture (normalized px on a
// 1080-wide slide) — mirrors slideOverlayOverridesSchema's sizePx bounds.
const RESIZE_MIN_SIZE_PX = 16;
const RESIZE_MAX_SIZE_PX = 160;

/** Final position + (optional) resized font size reported when a gesture ends. */
type OverlayGestureResult = { pos: { x: number; y: number }; sizePx?: number };

// Corner resize handles: rendered at the text block's corners while the block
// is selected or being manipulated. Each corner scales the text around the
// block's center, so all four behave symmetrically (pull away = larger).
const RESIZE_CORNERS = [
  { key: "tl", style: { left: 0, top: 0, transform: "translate(-50%, -50%)" }, cursor: "nwse-resize" },
  { key: "tr", style: { right: 0, top: 0, transform: "translate(50%, -50%)" }, cursor: "nesw-resize" },
  { key: "bl", style: { left: 0, bottom: 0, transform: "translate(-50%, 50%)" }, cursor: "nesw-resize" },
  { key: "br", style: { right: 0, bottom: 0, transform: "translate(50%, 50%)" }, cursor: "nwse-resize" },
] as const;

// Always-mounted drag/resize layer over a ready slide. When idle it renders an
// INVISIBLE hit region exactly where the composited text sits (same shared
// layout math the server uses); pressing down on it and dragging past a small
// threshold seamlessly enters move mode (parent swaps in the clean image, this
// layer's text becomes visible) and the SAME gesture continues — releasing
// auto-saves the dropped spot. A simple tap toggles a selected state that
// reveals corner handles; dragging a handle scales the font size live (lines
// re-wrap through the shared layout math) and releasing saves the new size.
//
// Performance: per-frame MOVE updates are applied directly to the DOM via a
// ref + requestAnimationFrame — no React state, no parent re-render, and no
// re-run of the line-layout math. RESIZE updates set one local state per frame
// (the wrap must re-run), which re-renders only this small layer. The parent
// is notified exactly three times per gesture: start, end/cancel.
export function SlideMoveOverlay({
  slide, run, settings, canvas = DEFAULT_SLIDE_CANVAS, moving, onGestureStart, onGestureEnd, onGestureCancel, saving,
}: {
  slide: SlideshowExpressSlide;
  run: SlideshowExpressRun;
  settings: SlideshowTextSettings | null;
  canvas?: SlideCanvas;
  moving: boolean;
  onGestureStart: () => void;
  onGestureEnd: (result: OverlayGestureResult) => void;
  onGestureCancel: () => void;
  saving: boolean;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const [frameW, setFrameW] = useState(0);
  const [selected, setSelected] = useState(false);
  const [manipulating, setManipulating] = useState<null | "move" | "resize">(null);
  // Live font size during a resize gesture (normalized px on a 1080 slide).
  const [liveSizePx, setLiveSizePx] = useState<number | null>(null);

  const gestureRef = useRef<{
    mode: "move" | "resize";
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPos: { x: number; y: number };
    startSizePx: number;
    centerClientX: number;
    centerClientY: number;
    startDist: number;
    started: boolean;
  } | null>(null);
  // Latest committed position/size for the in-flight session (gesture + save).
  const posRef = useRef<{ x: number; y: number } | null>(null);
  const sizeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => setFrameW(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // When the session ends (save finished/failed, cancel, or the carousel moved
  // to another slide mid-gesture), drop all live gesture state so the layer
  // falls back to the server-persisted truth.
  useEffect(() => {
    if (!moving) {
      gestureRef.current = null;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      posRef.current = null;
      sizeRef.current = null;
      lastPointRef.current = null;
      setManipulating(null);
      setLiveSizePx(null);
    }
  }, [moving]);

  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  const model = useMemo(
    () => overlayPreviewModel(slide, run, settings, liveSizePx ?? undefined, canvas),
    [slide, run, settings, liveSizePx, canvas],
  );
  const baseSizePx = model.existing?.sizePx || model.style.sizePx;
  const defaultPos = useMemo(() => initialOverlayPos(slide, run, settings, canvas), [slide, run, settings, canvas]);
  const k = frameW / canvas.width;
  const frameH = frameW * (canvas.height / canvas.width);
  const { layout, align } = model;

  const inSession = moving || manipulating !== null;
  const activePos = inSession && posRef.current ? posRef.current : defaultPos;

  const anchorTranslate = align === "left"
    ? "translate(0, -50%)"
    : align === "right"
      ? "translate(-100%, -50%)"
      : "translate(-50%, -50%)";
  const transformFor = (p: { x: number; y: number }) =>
    `translate(${p.x * frameW}px, ${p.y * frameH}px) ${anchorTranslate}`;

  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

  // Apply the latest pointer sample. Runs at most once per animation frame.
  const applyPendingFrame = () => {
    rafRef.current = null;
    const g = gestureRef.current;
    const pt = lastPointRef.current;
    if (!g || !pt || !g.started || frameW === 0) return;
    if (g.mode === "move") {
      const next = {
        x: clamp(g.startPos.x + (pt.x - g.startClientX) / frameW, 0.05, 0.95),
        y: clamp(g.startPos.y + (pt.y - g.startClientY) / frameH, 0.02, 0.98),
      };
      posRef.current = next;
      // Direct DOM write — React never renders during a pure move.
      if (blockRef.current) blockRef.current.style.transform = transformFor(next);
    } else {
      const dist = Math.hypot(pt.x - g.centerClientX, pt.y - g.centerClientY);
      const factor = g.startDist > 0 ? dist / g.startDist : 1;
      const next = clamp(Math.round(g.startSizePx * factor), RESIZE_MIN_SIZE_PX, RESIZE_MAX_SIZE_PX);
      if (next !== sizeRef.current) {
        sizeRef.current = next;
        setLiveSizePx(next);
      }
    }
  };

  const beginGesture = (e: React.PointerEvent<HTMLElement>, mode: "move" | "resize") => {
    if (saving || !k) return;
    // Block the carousel's snap-scroll from hijacking this gesture (touch pans
    // are also suppressed via touch-action: none on these elements).
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const startPos = inSession && posRef.current ? posRef.current : defaultPos;
    const startSize = sizeRef.current ?? liveSizePx ?? baseSizePx;
    const blockRect = blockRef.current?.getBoundingClientRect();
    const cx = blockRect ? blockRect.left + blockRect.width / 2 : e.clientX;
    const cy = blockRect ? blockRect.top + blockRect.height / 2 : e.clientY;
    gestureRef.current = {
      mode,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startPos,
      startSizePx: startSize,
      centerClientX: cx,
      centerClientY: cy,
      startDist: Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy)),
      started: false,
    };
    posRef.current = startPos;
    sizeRef.current = startSize;
    lastPointRef.current = { x: e.clientX, y: e.clientY };
  };

  const onGesturePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    const dx = e.clientX - g.startClientX;
    const dy = e.clientY - g.startClientY;
    if (!g.started) {
      // Small threshold so a simple tap/click never enters move/resize mode.
      if (Math.hypot(dx, dy) < 4) return;
      g.started = true;
      setSelected(true);
      setManipulating(g.mode);
      onGestureStart();
    }
    lastPointRef.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(applyPendingFrame);
  };

  const onGesturePointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    if (!g.started) {
      // Tap: toggle the selected state (shows/hides the corner handles). A tap
      // on a handle itself is a no-op so the handles don't vanish under the
      // user's finger. Neither ever enters move/resize mode.
      gestureRef.current = null;
      if (g.mode === "move") setSelected((s) => !s);
      return;
    }
    // Flush the final pointer sample synchronously before ending the gesture.
    lastPointRef.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    applyPendingFrame();
    const finalPos = posRef.current ?? g.startPos;
    const finalSize = sizeRef.current ?? g.startSizePx;
    gestureRef.current = null;
    onGestureEnd({
      pos: finalPos,
      // Only persist a size when a resize gesture actually changed it — a pure
      // move never touches the sizePx override (merge preserves any existing).
      sizePx: g.mode === "resize" && finalSize !== g.startSizePx ? finalSize : undefined,
    });
  };

  const onGesturePointerCancel = (e: React.PointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    gestureRef.current = null;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (g.started) onGestureCancel();
  };

  if (!k) {
    // Still need the measuring frame in the tree before the first paint.
    return <div ref={frameRef} className="pointer-events-none absolute inset-0" />;
  }

  const showHandles = !saving && (selected || manipulating !== null);
  const handleHit = Math.max(18, Math.round(22 * k * 3.6)); // touch-friendly hit area
  const handleDot = Math.max(8, Math.round(handleHit * 0.45));

  return (
    <div ref={frameRef} className="pointer-events-none absolute inset-0">
      <div
        ref={blockRef}
        role="button"
        aria-label="Drag to move slide text; drag a corner handle to resize"
        onPointerDown={(e) => beginGesture(e, "move")}
        onPointerMove={onGesturePointerMove}
        onPointerUp={onGesturePointerUp}
        onPointerCancel={onGesturePointerCancel}
        className={cn(
          "absolute left-0 top-0 z-10 select-none rounded-sm",
          (moving || selected) && "outline-dashed outline-1 outline-white/70",
        )}
        style={{
          transform: transformFor(activePos),
          touchAction: "none",
          cursor: saving ? "default" : manipulating === "move" ? "grabbing" : "grab",
          pointerEvents: saving ? "none" : "auto",
          padding: `${Math.max(2, 4 * k)}px`,
          maxWidth: "none",
          willChange: manipulating ? "transform" : undefined,
        }}
        data-testid={`overlay-move-text-${slide.id}`}
      >
        {/* Idle: invisible text over the composited copy (pure hit region — the
            baked-in text underneath stays the visible source of truth). During
            move/resize/save the parent shows the CLEAN image and this
            client-rendered text becomes the live preview. */}
        <div style={{ opacity: moving ? 1 : 0 }}>
          {layout.lines.map((line, i) => (
            <div
              key={i}
              style={{
                fontFamily: model.fontFamily,
                fontWeight: model.fontWeight as any,
                color: model.color,
                fontSize: `${layout.fontSize * k}px`,
                lineHeight: `${layout.lineHeightPx * k}px`,
                letterSpacing: `${model.style.letterSpacingEm}em`,
                textAlign: align,
                whiteSpace: "nowrap",
                textShadow: `0 ${Math.max(1, Math.round(layout.fontSize * 0.06) * k)}px ${Math.max(1, Math.round(layout.fontSize * 0.09) * k)}px rgba(0,0,0,0.55)`,
                WebkitTextStroke: `${Math.max(0.5, layout.fontSize * 0.02 * k)}px rgba(0,0,0,0.85)`,
                paintOrder: "stroke fill",
              }}
            >
              {line}
            </div>
          ))}
        </div>
        {showHandles && RESIZE_CORNERS.map((c) => (
          <div
            key={c.key}
            onPointerDown={(e) => beginGesture(e, "resize")}
            onPointerMove={onGesturePointerMove}
            onPointerUp={onGesturePointerUp}
            onPointerCancel={onGesturePointerCancel}
            className="absolute z-20 flex items-center justify-center"
            style={{
              ...c.style,
              width: `${handleHit}px`,
              height: `${handleHit}px`,
              cursor: c.cursor,
              touchAction: "none",
            }}
            aria-label={`Resize slide text (${c.key} corner)`}
            data-testid={`overlay-resize-${c.key}-${slide.id}`}
          >
            <div
              className="rounded-full border border-black/60 bg-white shadow-sm"
              style={{ width: `${handleDot}px`, height: `${handleDot}px` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideFrame({
  slide, run, settings, moving, onGestureStart, onGestureEnd, onGestureCancel, savingPosition,
}: {
  slide: SlideshowExpressSlide;
  run: SlideshowExpressRun;
  settings: SlideshowTextSettings | null;
  moving?: boolean;
  onGestureStart?: () => void;
  onGestureEnd?: (result: OverlayGestureResult) => void;
  onGestureCancel?: () => void;
  savingPosition?: boolean;
}) {
  const { toast } = useToast();
  const [canvas, setCanvas] = useState<SlideCanvas>(DEFAULT_SLIDE_CANVAS);
  const ready = slide.status === "ready" && !!slide.compositedImageUrl;
  const failed = slide.status === "failed";
  // A slide stuck non-terminal (pending/rendering/overlaying) while its run
  // is already "failed" is a retry stranded by a server restart — boot resume
  // deliberately ignores failed runs. Offer a Resume control that hits the
  // same retry endpoint: the server re-claims the slide and resumes the
  // persisted provider task (poll only — never a second paid render); if a
  // retry is genuinely still running, the server answers 409 and nothing is
  // disturbed.
  const stranded = !ready && !failed && run.status === "failed";
  // Task #2807 — retry JUST this slide (backend re-renders it through the
  // KIE → Fal fallback chain; the other slides are untouched). The 5s run
  // polling picks up the rendering → ready/failed transition.
  const retrySlide = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${run.brandId}/slideshow-express/runs/${run.id}/slides/${slide.id}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${run.brandId}/slideshow-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${run.brandId}/slideshow-express/runs`, run.id] });
      toast({ title: "Retrying slide", description: "Re-rendering just this slide." });
    },
    onError: (e: any) => toast({ title: "Retry failed", description: e?.message || "Could not retry this slide", variant: "destructive" }),
  });
  const draggable = ready && !!slide.rawImageUrl && !!(slide.slideText || "").trim()
    && !!onGestureStart && !!onGestureEnd && !!onGestureCancel;
  // Move/resize mode (active gesture or save-in-flight): CLEAN image + client
  // text layer.
  const showClean = draggable && !!moving;
  return (
    <div className="flex h-full w-full items-center justify-center bg-black">
      <div className="relative w-full" style={{ aspectRatio: `${canvas.width} / ${canvas.height}` }}>
      {ready ? (
        <img
          src={showClean ? slide.rawImageUrl! : slide.compositedImageUrl!}
          alt={showClean ? "Slide without text" : slide.slideText || "Slide"}
          className="h-full w-full object-cover"
          onLoad={(event) => {
            const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
            if (width > 0 && height > 0 && (width !== canvas.width || height !== canvas.height)) {
              setCanvas({ width, height });
            }
          }}
          data-testid={showClean ? `img-slide-raw-${slide.id}` : `img-slide-${slide.id}`}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center">
          {failed ? (
            <>
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <p className="text-[11px] text-white/70 line-clamp-3">{slide.error || "Failed"}</p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-1 h-7 px-3 text-[11px]"
                onClick={() => retrySlide.mutate()}
                disabled={retrySlide.isPending}
                data-testid={`button-retry-slide-${slide.id}`}
              >
                {retrySlide.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1 h-3 w-3" />} Retry
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-white/70" />
              <p className="text-[11px] text-white/70">Rendering…</p>
              {stranded && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-1 h-7 px-3 text-[11px]"
                  onClick={() => retrySlide.mutate()}
                  disabled={retrySlide.isPending}
                  data-testid={`button-resume-slide-${slide.id}`}
                >
                  {retrySlide.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1 h-3 w-3" />} Resume
                </Button>
              )}
            </>
          )}
        </div>
      )}
      {draggable && (
        <SlideMoveOverlay
          slide={slide}
          run={run}
          settings={settings}
            canvas={canvas}
          moving={!!moving}
          onGestureStart={onGestureStart!}
          onGestureEnd={onGestureEnd!}
          onGestureCancel={onGestureCancel!}
          saving={!!savingPosition}
        />
      )}
      {savingPosition && (
        <div
          className="absolute left-1/2 top-[76px] z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-[11px] font-medium text-white"
          data-testid={`status-saving-position-${slide.id}`}
        >
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </div>
      )}
      </div>
    </div>
  );
}

function PackageBlock({
  icon: Icon, title, value, onCopy, testid,
}: {
  icon: typeof MessageSquare;
  title: string;
  value: string;
  onCopy: () => void;
  testid: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onCopy} disabled={!value} data-testid={`button-copy-${testid}`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
      <p className="whitespace-pre-wrap text-sm" data-testid={testid}>{value || <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}
